import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, get, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useCallback, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { COLORS } from "@tCentro/theme/design";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import PeopleIcon from "@tCentro/Icons/SMEAdminPeopleIcon";
import SMEAdminHomeIcon from "@tCentro/Icons/SMEAdminHomeIcon";
import addBranchData, { AddBranchParameter } from "@tCentro/Handlers/CENAdminAddBranchHandler";
import { BranchModel } from "@tCentro/Services/CENAdminBranchService";
import getBranchCollectionData, {
    GetBranchCollectionParameter,
} from "@tCentro/Handlers/CENAdminGetBranchCollectionHandler";
import { BranchCollectionModel } from "@tCentro/Services/CENAdminBranchService";
import updateBranchData, { UpdateBranchParameter } from "@tCentro/Handlers/CENAdminUpdateBranchHandler";
import deleteBranchData, { DeleteBranchParameter } from "@tCentro/Handlers/CENAdminDeleteBranchHandler";
import addProductTypeData, { AddProductTypeParameter } from "@tCentro/Handlers/CENAdminAddProductTypeHandler";
import getProductTypeCollectionData, {
    GetProductTypeCollectionParameter,
} from "@tCentro/Handlers/CENAdminGetProductTypeCollectionHandler";
import updateProductTypeData, { UpdateProductTypeParameter } from "@tCentro/Handlers/CENAdminUpdateProductTypeHandler";
import deleteProductTypeData, { DeleteProductTypeParameter } from "@tCentro/Handlers/CENAdminDeleteProductTypeHandler";
import getProductTypeById, {
    GetProductTypeParameter,
} from "@tCentro/Handlers/CENAdminGetProductTypeByIdCollectionHandler";
import { ProductTypeModel, ProductTypeCollectionModel } from "@tCentro/Services/CENAdminProductTypeService";
import addDivisionData, { AddDivisionParameter } from "@tCentro/Handlers/CENAdminAddDivisionHandler";
import getDivisionCollectionData, {
    GetDivisionCollectionParameter,
} from "@tCentro/Handlers/CENAdminGetDivisionCollectionHandler";
import updateDivisionData, { UpdateDivisionParameter } from "@tCentro/Handlers/CENAdminUpdateDivisionHandler";
import deleteDivisionData, { DeleteDivisionParameter } from "@tCentro/Handlers/CENAdminDeleteDivisionHandler";
import { DivisionModel, DivisionCollectionModel } from "@tCentro/Services/CENAdminDivisionService";
import addEmployeeData, { AddEmployeeParameter } from "@tCentro/Handlers/CENAdminAddEmployeeHandler";
import getEmployeeCollectionData, {
    GetEmployeeCollectionParameter,
} from "@tCentro/Handlers/CENAdminGetEmployeeCollectionHandler";
import updateEmployeeData, { UpdateEmployeeParameter } from "@tCentro/Handlers/CENAdminUpdateEmployeeHandler";
import deleteEmployeeData, { DeleteEmployeeParameter } from "@tCentro/Handlers/CENAdminDeleteEmployeeHandler";
import { EmployeeModel, EmployeeCollectionModel } from "@tCentro/Services/CENAdminEmployeeService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import getBrandsCollectionData, {
    GetBrandsCollectionParameter,
} from "@tCentro/Handlers/CENAdminGetBrandsCollectionHandler";

import { BrandsCollectionModel, BrandsModel } from "@tCentro/Services/CENAdminBrandsService";
import getEmployeesById, { GetEmployeeByIdParameter } from "@tCentro/Handlers/CENAdminGetEmployeeById";
import { BrandsPageContext } from "@insite/content-library/Pages/BrandsPage";
import getBranchByIdHandler, { GetBranchByIdParameter } from "@tCentro/Handlers/CENAdminGetBranchById";
import getDivisionByIdHandler, { GetDivisionByIdParameter } from "@tCentro/Handlers/CENAdminGetDivisionById";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import getBrandsList, { GetBrandListParameter } from "@tCentro/Handlers/CENAdminGetBrandsListHandler";
import { BrandListModel, BrandsSmeModel } from "@tCentro/Services/CENAdminBrandListService";
import getBrandListsById, { GetBrandListIdParameter } from "@tCentro/Handlers/CENAdminGetBrandsById";
import AvatarPlaceholder from "@tCentro/Icons/AvatarPlaceholder";

// ─── FIELD ENUM ───────────────────────────────────────────────────────────────

const enum fields {
    initialPage = "initialPage",
    showSearchBar = "showSearchBar",
    showDashboardStats = "showDashboardStats",
    adminEmail = "adminEmail",
    adminDisplayName = "adminDisplayName",
}

// ─── PROPS ────────────────────────────────────────────────────────────────────

interface OwnProps extends WidgetProps {
    fields: {
        [fields.initialPage]: "employees" | "branches" | "divisions" | "brands" | "products";
        [fields.showSearchBar]: boolean;
        [fields.showDashboardStats]: boolean;
        [fields.adminEmail]: string;
        [fields.adminDisplayName]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    session: getSession(state),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
});
const mapDispatchToProps = {
    addBranchData,
    getBranchCollectionData,
    updateBranchData,
    deleteBranchData,
    addProductTypeData,
    getProductTypeCollectionData,
    updateProductTypeData,
    getProductTypeById,
    deleteProductTypeData,
    addDivisionData,
    getDivisionCollectionData,
    updateDivisionData,
    deleteDivisionData,
    addEmployeeData,
    getEmployeeCollectionData,
    updateEmployeeData,
    deleteEmployeeData,
    getBrandsCollectionData,
    getEmployeesById,
    getBranchByIdHandler,
    getDivisionByIdHandler,
    getBrandListsById,
    getBrandsList,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

// ─── STYLES INTERFACE ─────────────────────────────────────────────────────────

export interface AdminPortalStyles {
    portalWrapper?: InjectableCss;
    sidebarWrapper?: InjectableCss;
    sidebarLogoWrapper?: InjectableCss;
    sidebarNavWrapper?: InjectableCss;
    sidebarFooterWrapper?: InjectableCss;
    mainContentWrapper?: InjectableCss;
    pageHeaderWrapper?: InjectableCss;
    pageHeaderTitle?: TypographyProps;
    pageHeaderSubtitle?: TypographyProps;
    sectionTitle?: TypographyProps;
    tableWrapper?: InjectableCss;
    tableHeaderWrapper?: InjectableCss;
    statCardsContainer?: GridContainerProps;
    statCardItem?: GridItemProps;
    statCardWrapper?: InjectableCss;
    dashboardPanelsContainer?: GridContainerProps;
    dashboardPanelItem?: GridItemProps;
    dashboardPanelWrapper?: InjectableCss;
    dashboardPanelTitle?: TypographyProps;
    searchBarWrapper?: InjectableCss;
    modalOverlayWrapper?: InjectableCss;
    modalContentWrapper?: InjectableCss;
    modalHeaderWrapper?: InjectableCss;
    modalFooterWrapper?: InjectableCss;
    formGridContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    formGridItemHalf?: GridItemProps;
    formGridItemThird?: GridItemProps;
    togglesGridContainer?: GridContainerProps;
    togglesGridItem?: GridItemProps;
    gridPaddingAdjust?: GridItemProps;
    dividerWrapper?: InjectableCss;
    confirmModalWrapper?: InjectableCss;
    confirmModalIconWrapper?: InjectableCss;
    actionButtonsWrapper?: InjectableCss;
    buttonsWrapper?: ButtonPresentationProps;
    titleIcon?: IconPresentationProps;
    paginationContainer?: GridContainerProps;
    paginationGridItem?: GridItemProps;
    pagination?: PaginationPresentationProps;
    customCss?: InjectableCss;
    employeWrapper?: InjectableCss;
}

interface BrandItem {
    id: string;
    name: string;
    is_deleted?: boolean;
}

interface BranchesPageProps {
    showSearch: boolean;
    addBranchData: (parameter: AddBranchParameter) => void;
    getBranchCollectionData: (parameter: GetBranchCollectionParameter) => void;
    updateBranchData: (parameter: UpdateBranchParameter) => void;
    deleteBranchData: (parameter: DeleteBranchParameter) => void;
    onBranchesLoaded?: (branches: BrandItem[]) => void;
    getBranchByIdHandler: (parameter: GetBranchByIdParameter) => void;
    signInUrl?: string;
}

interface ProductTypesPageProps {
    addProductTypeData: (parameter: AddProductTypeParameter) => void;
    getProductTypeById: (parameter: GetProductTypeParameter) => void;
    getProductTypeCollectionData: (parameter: GetProductTypeCollectionParameter) => void;
    updateProductTypeData: (parameter: UpdateProductTypeParameter) => void;
    deleteProductTypeData: (parameter: DeleteProductTypeParameter) => void;
    divisions: { id: string; name: string; is_deleted: boolean }[];
    getDivisionCollectionData: Function;
    signInUrl?: string;
    showSearch?: boolean;
}

interface BrandsPageProps {
    showSearch?: boolean;
    getBrandsList: (parameter: GetBrandListParameter) => void;
    getBrandListsById: (parameter: GetBrandListIdParameter) => void;
}
interface DivisionsPageProps {
    addDivisionData: (parameter: AddDivisionParameter) => void;
    getDivisionCollectionData: (parameter: GetDivisionCollectionParameter) => void;
    updateDivisionData: (parameter: UpdateDivisionParameter) => void;
    deleteDivisionData: (parameter: DeleteDivisionParameter) => void;
    onDivisionsLoaded?: (divisions: { id: string; name: string; is_deleted: boolean }[]) => void;
    getBrandsCollectionData: (parameter: GetBrandsCollectionParameter) => void;
    getDivisionByIdHandler: (parameter: GetDivisionByIdParameter) => void;
    signInUrl?: string;
}

interface EmployeesPageProps {
    showSearch: boolean;
    addEmployeeData: (parameter: AddEmployeeParameter) => void;
    getEmployeeCollectionData: (parameter: GetEmployeeCollectionParameter) => void;
    updateEmployeeData: (parameter: UpdateEmployeeParameter) => void;
    deleteEmployeeData: (parameter: DeleteEmployeeParameter) => void;
    getBranchCollectionData: (parameter: GetBranchCollectionParameter) => void;
    getDivisionCollectionData: (parameter: GetDivisionCollectionParameter) => void; // ← add
    getProductTypeCollectionData: (parameter: GetProductTypeCollectionParameter) => void;
    getBrandsCollectionData: (parameter: GetBrandsCollectionParameter) => void;
    getEmployeesById: (parameter: GetEmployeeByIdParameter) => void;
    signInUrl?: string;
}

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────

const T = {
    white: "#ffffff",
    gray50: "#f8f9fb",
    gray100: "#f0f2f5",
    gray200: "#e2e6ed",
    gray300: "#c8cfd9",
    gray400: "#9aa3b0",
    gray500: "#6b7585",
    gray600: "#4a5263",
    gray700: "#333b4a",
    gray800: "#1e2433",
    gray900: "#111827",
    blue50: "#eff6ff",
    blue100: "#dbeafe",
    blue500: "#3b6ef5",
    blue600: "#2457e0",
    green50: "#f0fdf4",
    green600: "#16a34a",
    amber50: "#fffbeb",
    red50: "#fef2f2",
    red500: "#ef4444",
    red600: "#dc2626",
    shadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
    shadowMd: "0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)",
    shadowLg: "0 20px 48px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.06)",
};

// ─── EXPORTED STYLES ──────────────────────────────────────────────────────────

export const adminPortalStyles: AdminPortalStyles = {
    customCss: {
        css: css`
            button {
                min-height: 20px;
                height: 20px;
                min-width: 32px;
                padding: 0px !important;
                background: white !important;
                color: ${COLORS.background.tag};
                border: none !important;
                box-shadow: none !important;
            }
        `,
    },
    employeWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: 1200px;
                    `,
                    css`
                        min-width: 1200px;
                    `,
                    css`
                        min-width: 1200px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    paginationContainer: {
        css: css`
            margin: 0;
        `,
    },
    paginationGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                nav {
                    justify-content: center;
                }
                @media print {
                    display: none;
                }
            `,
        },
    },
    titleIcon: {
        size: 24,
        css: css`
            svg {
                fill: ${getColor("primary")};
                stroke: ${getColor("primary")};

                height: 100%;
                width: 100%;
                ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                    css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                    css`
                            fill: ${get(theme, "colors.info.contrast")};
                            stroke: none;
                        `,
                    css`
                            @media (min-width: 993px) and (max-width: 1079px) {
                                fill: ${get(theme, "colors.info.contrast")};
                                stroke: none;
                            }
                        `,
                ]);
            }}
            }
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex !important;
            align-items: center;
            gap: 9px;
            width: 100%;
            padding: 8px 10px;
            border-radius: 7px;
            border: none;
            cursor: pointer;
            background: white;
            color: ${COLORS.background.tag};
            font-size: 13px;
            font-weight: 700;
            text-align: left;
            transition: all 0.12s;
            &:focus {
                utline-offset: 1px;
                outline-color: ${COLORS.primary.light} !important;
                outline-style: none !important;
                outline-width: 2px;
            }
        `,
        // borderLeft: `2px solid ${on ? T.blue500 : "transparent"}`,`,
    },
    portalWrapper: {
        css: css`
            display: flex;
            height: 100vh;
            background: ${T.gray50};
            font-family: "IBM Plex Sans", "Helvetica Neue", sans-serif;
            color: ${T.gray800};
            overflow: hidden;
            max-width: 1800px;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    sidebarWrapper: {
        css: css`
            width: 222px;
            background: ${T.white};
            border-right: 1px solid ${T.gray200};
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        border-right: none;
                        border-bottom: 1px solid ${T.gray200};
                    `,
                    css`
                        width: 100%;
                        border-right: none;
                        border-bottom: 1px solid ${T.gray200};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    sidebarLogoWrapper: {
        css: css`
            padding: 20px 18px 16px;
            border-bottom: 1px solid ${T.gray100};
            display: flex;
            align-items: center;
            gap: 10px;
        `,
    },

    sidebarNavWrapper: {
        css: css`
            flex: 1;
            padding: 10px;
            display: flex;
            flex-direction: column;
            gap: 1px;
            overflow-y: auto;
            background: white;
        `,
    },

    sidebarFooterWrapper: {
        css: css`
            padding: 12px 14px;
            border-top: 1px solid ${T.gray100};
            display: flex;
            align-items: center;
            gap: 9px;
        `,
    },

    mainContentWrapper: {
        css: css`
            flex: 1;
            overflow: auto;
            padding: 30px 34px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 16px;
                    `,
                    css`
                        padding: 20px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },

    pageHeaderWrapper: {
        css: css`
            margin-bottom: 22px;
        `,
    },

    pageHeaderTitle: {
        variant: "h2",
        css: css`
            margin: 0 0 3px;
            font-size: 21px;
            font-weight: 800;
            color: ${getProp("theme.typography.h2.color")};
            letter-spacing: -0.02em;
        `,
    },

    pageHeaderSubtitle: {
        variant: "p",
        css: css`
            margin: 0;
            font-size: 12.5px;
            color: ${T.gray400};
        `,
    },

    sectionTitle: {
        variant: "h2",
        css: css`
            margin: 0 0 3px;
            font-size: 21px;
            font-weight: 800;
            letter-spacing: -0.02em;
            color: ${getProp("theme.typography.h2.color")};
        `,
    },

    tableWrapper: {
        css: css`
            background: ${T.white};
            border: 1px solid ${T.gray200};
            border-radius: 10px;
            overflow: hidden;
            box-shadow: ${T.shadow};
            width: 100%;
        `,
    },

    tableHeaderWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            margin-bottom: 14px;
            flex-wrap: wrap;
            gap: 12px;
        `,
    },

    statCardsContainer: {
        gap: 14,
        css: css`
            margin-bottom: 26px;
        `,
    },

    statCardItem: {
        width: [12, 6, 6, 3, 3],
    },

    statCardWrapper: {
        css: css`
            background: ${T.white};
            border: 1px solid ${T.gray200};
            border-radius: 10px;
            padding: 17px 18px;
            box-shadow: ${T.shadow};
            display: flex;
            align-items: center;
            gap: 13px;
            height: 100%;
        `,
    },

    dashboardPanelsContainer: {
        gap: 18,
    },

    dashboardPanelItem: {
        width: [12, 12, 12, 6, 6],
    },

    dashboardPanelWrapper: {
        css: css`
            background: ${T.white};
            border: 1px solid ${T.gray200};
            border-radius: 10px;
            overflow: hidden;
            box-shadow: ${T.shadow};
            height: 100%;
        `,
    },

    dashboardPanelTitle: {
        variant: "h6",
        css: css`
            margin: 0;
            font-size: 13px;
            font-weight: 700;
            color: ${T.gray800};
        `,
    },

    searchBarWrapper: {
        css: css`
            position: relative;
            max-width: 300px;
            margin-top: 14px;
        `,
    },

    modalOverlayWrapper: {
        css: css`
            position: fixed;
            inset: 0;
            background: rgba(17, 24, 39, 0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            backdrop-filter: blur(3px);
            padding: 20px;
        `,
    },

    modalContentWrapper: {
        css: css`
            background: ${T.white};
            border-radius: 12px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            box-shadow: ${T.shadowLg};
            border: 1px solid ${T.gray200};
        `,
    },

    modalHeaderWrapper: {
        css: css`
            padding: 20px 24px 17px;
            border-bottom: 1px solid ${T.gray100};
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            flex-shrink: 0;
            min-height: 72px;
        `,
    },

    modalFooterWrapper: {
        css: css`
            padding: 14px 24px;
            border-top: 1px solid ${T.gray100};
            background: ${T.gray50};
            display: flex;
            justify-content: flex-end;
            gap: 9px;
        `,
    },

    formGridContainer: {
        gap: 14,
    },

    formGridItem: {
        width: [12, 12, 6, 6, 6],
    },

    formGridItemHalf: {
        width: [12, 12, 6, 6, 6],
    },

    formGridItemThird: {
        width: [12, 12, 4, 4, 4],
    },

    togglesGridContainer: {
        gap: 0,
        css: css`
            background: ${T.gray50};
            border-radius: 8px;
            border: 1px solid ${T.gray200};
            padding: 2px 16px;
        `,
    },

    togglesGridItem: {
        width: [12, 12, 12, 12, 12],
    },

    gridPaddingAdjust: {
        css: css`
            padding: 15px !important;
        `,
    },

    dividerWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 20px 0 16px;
        `,
    },

    confirmModalWrapper: {
        css: css`
            position: fixed;
            inset: 0;
            background: rgba(17, 24, 39, 0.45);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            backdrop-filter: blur(3px);
        `,
    },

    confirmModalIconWrapper: {
        css: css`
            width: 36px;
            height: 36px;
            border-radius: 9px;
            background: ${T.red50};
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        `,
    },

    actionButtonsWrapper: {
        css: css`
            display: flex;
            gap: 6px;
            justify-content: flex-end;
        `,
    },
};

const styles = adminPortalStyles;

// ─── ICONS ────────────────────────────────────────────────────────────────────

interface IcoProps {
    d: string;
    size?: number;
    color?: string;
    sw?: number;
}
const Ico: FC<IcoProps> = ({ d, size = 16, color = "currentColor", sw = 1.75 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}
    >
        <path d={d} />
    </svg>
);
const IC: Record<string, string> = {
    dash: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
    emp: "M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75",
    branch: "M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2zM9 22V12h6v10",
    div: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
    brand: "M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01",
    product:
        "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
    plus: "M12 5v14M5 12h14",
    edit: "M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z",
    trash: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
    search: "M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z",
    check: "M5 13l4 4L19 7",
    x: "M6 18L18 6M6 6l12 12",
    save: "M17 21v-7H7v7M7 3v4h8M5 3h11l3 3v15a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z",
    alert: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z",
    link: "M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71",
};

// ─── BADGE ────────────────────────────────────────────────────────────────────

interface BadgeProps {
    children: React.ReactNode;
    variant?: "default" | "blue" | "green" | "amber" | "red" | "ghost";
}
const Badge: FC<BadgeProps> = ({ children, variant = "default" }) => {
    const v: Record<string, { bg: string; color: string; border: string }> = {
        default: { bg: T.gray100, color: T.gray600, border: T.gray200 },
        blue: { bg: T.blue50, color: T.blue600, border: T.blue100 },
        green: { bg: T.green50, color: T.green600, border: "#bbf7d0" },
        amber: { bg: T.amber50, color: "#92400e", border: "#fde68a" },
        red: { bg: T.red50, color: T.red600, border: "#fecaca" },
        ghost: { bg: "transparent", color: T.gray400, border: T.gray200 },
    };
    const s = v[variant] || v.default;
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 8px",
                borderRadius: 5,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.03em",
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
};

// ─── BUTTON ───────────────────────────────────────────────────────────────────

interface BtnProps {
    children?: React.ReactNode;
    onClick?: () => void;
    variant?: "primary" | "secondary" | "ghost" | "danger";
    size?: "sm" | "md" | "lg";
    icon?: string;
    disabled?: boolean;
}
const Btn: FC<BtnProps> = ({ children, onClick, variant = "primary", size = "sm", icon, disabled }) => {
    const [hov, setHov] = useState(false);
    const sv: Record<string, { bg: string; color: string; border: string; shadow: string }> = {
        primary: {
            bg: hov ? COLORS.primary.dark : COLORS.primary.base,
            color: COLORS.primary.contrast,
            border: COLORS.primary.dark,
            shadow: "0 1px 3px rgba(59,110,245,0.3)",
        },
        secondary: {
            bg: hov ? COLORS.background.hover : COLORS.white,
            color: COLORS.text.base,
            border: COLORS.border.base,
            shadow: "none",
        },
        ghost: {
            bg: hov ? COLORS.background.hover : "transparent",
            color: COLORS.text.lighter,
            border: "transparent",
            shadow: "none",
        },
        danger: {
            bg: hov ? COLORS.error : COLORS.background.shade,
            color: hov ? COLORS.white : COLORS.error,
            border: hov ? COLORS.error : COLORS.border.dark,
            shadow: "none",
        },
    };
    const s = sv[variant] || sv.primary;
    const pad = { sm: "5px 11px", md: "8px 15px", lg: "10px 20px" }[size] || "8px 15px";
    const fs = { sm: 12, md: 13, lg: 14 }[size] || 13;
    return (
        <Button
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: pad,
                borderRadius: 7,
                fontSize: fs,
                fontWeight: 600,
                background: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.55 : 1,
                boxShadow: s.shadow,
                transition: "all 0.13s",
                letterSpacing: "0.01em",
                lineHeight: 1,
            }}
        >
            {icon && <Ico d={IC[icon]} size={13} color={s.color} sw={2} />}
            {children}
        </Button>
    );
};

// ─── FORM PRIMITIVES ──────────────────────────────────────────────────────────

interface FieldProps {
    label?: string;
    required?: boolean;
    hint?: string;
    error?: string;
    children: React.ReactNode;
}
const Field: FC<FieldProps> = ({ label, required, hint, error, children }) => (
    <StyledWrapper style={{ display: "flex", flexDirection: "column", gap: 5, width: "100%" }}>
        {label && (
            <label
                style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: error ? T.red500 : T.gray500, // ← red label on error
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                }}
            >
                {label}
                {required && <span style={{ color: T.red500 }}> *</span>}
            </label>
        )}
        {children}
        {error && <span style={{ fontSize: 11, color: T.red500, marginTop: 1, fontWeight: 600 }}>{error}</span>}
        {!error && hint && <span style={{ fontSize: 11, color: T.gray400, marginTop: 1 }}>{hint}</span>}
    </StyledWrapper>
);

interface InputProps {
    label?: string;
    value: string;
    onChange: (v: string) => void;
    type?: string;
    placeholder?: string;
    required?: boolean;
    hint?: string;
    disabled?: boolean;
    error?: string;
}
const Input: FC<InputProps> = ({
    label,
    value,
    onChange,
    type = "text",
    placeholder,
    required,
    hint,
    disabled,
    error,
}) => {
    const [foc, setFoc] = useState(false);
    return (
        <Field label={label} required={required} hint={hint}>
            <input
                type={type}
                value={value ?? ""}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={() => setFoc(true)}
                onBlur={() => setFoc(false)}
                style={{
                    background: disabled ? T.gray50 : T.white,
                    border: `1.5px solid ${error ? T.red500 : foc ? T.blue500 : T.gray300}`, // ← red when error
                    borderRadius: 7,
                    padding: "9px 11px",
                    color: T.gray800,
                    fontSize: 13,
                    outline: "none",
                    width: "100%",
                    boxSizing: "border-box",
                    // boxShadow: error ? `0 0 0 3px #ef4444` : foc ? `0 0 0 3px ${T.blue100}` : "none", // ← red glow when error
                    transition: "all 0.13s",
                }}
            />
            {error && (
                <Typography
                    style={{
                        display: "block",
                        marginTop: 5,
                        fontSize: 12,
                        color: T.red500,
                    }}
                >
                    {error}
                </Typography>
            )}
        </Field>
    );
};

interface TextareaInputProps {
    label?: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    required?: boolean;
    error?: string; // ← add this
    rows?: number;
}
const TextareaInput: FC<TextareaInputProps> = ({ label, value, onChange, placeholder, required, error, rows = 3 }) => {
    const [foc, setFoc] = useState(false);
    const hasError = !!error;
    return (
        <Field label={label} required={required} error={error}>
            <textarea
                value={value ?? ""}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                rows={rows}
                onFocus={() => setFoc(true)}
                onBlur={() => setFoc(false)}
                style={{
                    background: T.white,
                    border: `1.5px solid ${hasError ? T.red500 : foc ? T.blue500 : T.gray300}`,
                    borderRadius: 7,
                    padding: "9px 11px",
                    color: T.gray800,
                    fontSize: 13,
                    outline: "none",
                    width: "100%",
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    boxShadow: hasError ? `0 0 0 3px rgba(239,68,68,0.15)` : foc ? `0 0 0 3px ${T.blue100}` : "none",
                    transition: "all 0.13s",
                }}
            />
        </Field>
    );
};

interface SearchableSelectProps {
    label?: string;
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
    required?: boolean;
    error?: string;
    placeholder?: string;
    dropdownId?: string;
    openDropdown?: string | null;
    onOpenDropdown?: (id: string | null) => void;
}
function SearchableSelect({
    label,
    value,
    onChange,
    options,
    required,
    error,
    placeholder = "Search division…",
    dropdownId,
    openDropdown,
    onOpenDropdown,
}: SearchableSelectProps) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
                setQ("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const selected = options.find(o => o.value === value);
    const filtered = options.filter(o => o.label.toLowerCase().includes(q.toLowerCase()));

    return (
        <Field label={label} required={required}>
            <div style={{ position: "relative", width: "100%" }} ref={ref}>
                {/* Trigger */}
                <div
                    onClick={() => {
                        setOpen(o => !o);
                        setQ("");
                    }}
                    style={{
                        background: T.white,
                        border: `1.5px solid ${error ? T.red500 : open ? T.blue500 : T.gray300}`,
                        borderRadius: 7,
                        padding: "0 11px",
                        cursor: "pointer",
                        minHeight: 35,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        boxShadow: error ? `0 0 0 3px rgba(239,68,68,0.15)` : open ? `0 0 0 3px ${T.blue100}` : "none",
                        transition: "all 0.13s",
                        boxSizing: "border-box",
                    }}
                >
                    <span style={{ fontSize: 13, color: selected ? T.gray800 : T.gray400 }}>
                        {selected ? selected.label : `— ${translate("Select")} —`}
                    </span>
                    {selected && (
                        <span
                            onClick={e => {
                                e.stopPropagation();
                                onChange("");
                                setQ("");
                            }}
                            style={{ display: "flex", cursor: "pointer", flexShrink: 0 }}
                        >
                            <Ico d={IC.x} size={13} color={T.gray400} sw={2.5} />
                        </span>
                    )}
                    {!selected && (
                        <span style={{ flexShrink: 0 }}>
                            <Ico d="M19 9l-7 7-7-7" size={14} color={T.gray400} />
                        </span>
                    )}
                </div>

                {/* Dropdown */}
                {open && (
                    <div
                        style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            right: 0,
                            zIndex: 200,
                            background: T.white,
                            border: `1.5px solid ${T.gray200}`,
                            borderRadius: 8,
                            boxShadow: T.shadowMd,
                            overflow: "hidden",
                        }}
                    >
                        {/* Search box */}
                        <div
                            style={{
                                padding: "8px 10px",
                                borderBottom: `1px solid ${T.gray100}`,
                                position: "relative",
                            }}
                        >
                            <div
                                style={{
                                    position: "absolute",
                                    left: 19,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                }}
                            >
                                <Ico d={IC.search} size={13} color={T.gray400} />
                            </div>
                            <input
                                autoFocus
                                value={q}
                                onChange={e => setQ(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                placeholder={placeholder}
                                style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    background: T.gray50,
                                    border: `1.5px solid ${T.gray200}`,
                                    borderRadius: 6,
                                    padding: "6px 10px 6px 28px",
                                    fontSize: 12,
                                    color: T.gray700,
                                    outline: "none",
                                }}
                            />
                        </div>

                        {/* Options */}
                        <div style={{ maxHeight: 200, overflowY: "auto" }}>
                            {filtered.length === 0 && (
                                <div
                                    style={{
                                        padding: "12px 14px",
                                        fontSize: 12,
                                        color: T.gray400,
                                        fontStyle: "italic",
                                    }}
                                >
                                    {translate("No divisions match")} "{q}"
                                </div>
                            )}
                            {filtered.map(o => {
                                const isSelected = value === o.value;
                                return (
                                    <div
                                        key={o.value}
                                        onClick={() => {
                                            onChange(o.value);
                                            setOpen(false);
                                            setQ("");
                                        }}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10,
                                            padding: "9px 12px",
                                            cursor: "pointer",
                                            fontSize: 13,
                                            color: isSelected ? T.blue600 : T.gray700,
                                            background: isSelected ? T.blue50 : "transparent",
                                            transition: "background 0.1s",
                                        }}
                                        onMouseEnter={e => {
                                            if (!isSelected) e.currentTarget.style.background = T.gray50;
                                        }}
                                        onMouseLeave={e => {
                                            if (!isSelected) e.currentTarget.style.background = "transparent";
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: "50%",
                                                flexShrink: 0,
                                                background: isSelected ? T.blue500 : T.white,
                                                border: `1.5px solid ${isSelected ? T.blue500 : T.gray300}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "all 0.13s",
                                            }}
                                        >
                                            {isSelected && <Ico d={IC.check} size={9} color="#fff" sw={3} />}
                                        </div>
                                        {o.label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            {error && (
                <Typography style={{ display: "block", marginTop: 5, fontSize: 12, color: T.red500 }}>
                    {error}
                </Typography>
            )}
        </Field>
    );
}

interface SelectInputProps {
    label?: string;
    value: string;
    onChange: (v: string) => void;
    options: { value: string; label: string }[];
    required?: boolean;
    error?: string; // ← add this
}
const SelectInput: FC<SelectInputProps> = ({ label, value, onChange, options, required, error }) => {
    const [foc, setFoc] = useState(false);
    return (
        <Field label={label} required={required}>
            <select
                value={value ?? ""}
                onChange={e => onChange(e.target.value)}
                onFocus={() => setFoc(true)}
                onBlur={() => setFoc(false)}
                style={{
                    background: T.white,
                    border: `1.5px solid ${error ? T.red500 : foc ? T.blue500 : T.gray300}`, // ← red when error
                    borderRadius: 7,
                    padding: "9px 11px",
                    color: value ? T.gray800 : T.gray400,
                    fontSize: 13,
                    outline: "none",
                    width: "100%",
                    boxSizing: "border-box",
                    // boxShadow: error ? `0 0 0 3px ${T.red100}` : foc ? `0 0 0 3px ${T.blue100}` : "none", // ← red glow when error
                    transition: "all 0.13s",
                }}
            >
                <option value="">— {translate("Select")} —</option>
                {options.map(o => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
            {error && (
                <Typography
                    style={{
                        display: "block",
                        marginTop: 5,
                        fontSize: 12,
                        color: T.red500,
                    }}
                >
                    {error}
                </Typography>
            )}
        </Field>
    );
};

interface MultiSelectOption {
    value: string;
    label: string;
}
interface MultiSelectProps {
    label?: string;
    value?: string[];
    onChange: (v: string[]) => void;
    options: MultiSelectOption[];
    required?: boolean;
    error?: string;
    dropdownId?: string;
    openDropdown?: string | null;
    onOpenDropdown?: (id: string | null) => void;
    brandSettings?: any;
    onUpdateBrandSetting?: (brandId: string, key: string, val: boolean) => void;
}
function MultiSelect({
    label,
    value = [],
    onChange,
    options,
    required,
    error,

    // ✅ ADD HERE
    brandSettings = {},
    onUpdateBrandSetting,
}: MultiSelectProps) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const toggle = (id: string) => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
    const selected = options.filter(o => value.includes(o.value));
    const filtered = options
        .filter(o => o.label.toLowerCase().includes(q.toLowerCase()))
        .filter(o => !showSelectedOnly || value.includes(o.value));
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
                setQ("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    return (
        <Field label={label} required={required}>
            <div style={{ position: "relative" }} ref={ref}>
                {/* Trigger */}
                <div
                    onClick={() => {
                        setOpen(o => !o);
                        setQ("");
                    }}
                    style={{
                        background: T.white,
                        border: `1.5px solid ${error ? T.red500 : open ? T.blue500 : T.gray300}`,
                        borderRadius: 7,
                        padding: "7px 11px",
                        cursor: "pointer",
                        minHeight: 38,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 5,
                        alignItems: "center",
                        boxShadow: open ? `0 0 0 3px ${T.blue100}` : "none",
                        transition: "all 0.13s",
                        boxSizing: "border-box",
                    }}
                >
                    {selected.length === 0 && <span style={{ fontSize: 13, color: T.gray400 }}>— Select —</span>}
                    {selected.map(o => (
                        <span
                            key={o.value}
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                background: T.blue50,
                                color: T.blue600,
                                border: `1px solid ${T.blue100}`,
                                borderRadius: 5,
                                padding: "2px 7px",
                                fontSize: 12,
                                fontWeight: 600,
                            }}
                        >
                            {o.label}
                            <span
                                onClick={e => {
                                    e.stopPropagation();
                                    toggle(o.value);
                                }}
                                style={{ cursor: "pointer", lineHeight: 1, display: "flex" }}
                            >
                                <Ico d={IC.x} size={11} color={T.blue500} sw={2.5} />
                            </span>
                        </span>
                    ))}
                    <span style={{ marginLeft: "auto", flexShrink: 0 }}>
                        <Ico d="M19 9l-7 7-7-7" size={14} color={T.gray400} />
                    </span>
                </div>

                {/* Dropdown */}
                {open && (
                    <div
                        style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            right: 0,
                            zIndex: 200,
                            background: T.white,
                            border: `1.5px solid ${T.gray200}`,
                            borderRadius: 8,
                            boxShadow: T.shadowMd,
                            overflow: "hidden",
                        }}
                    >
                        {/* Search box */}
                        <div
                            style={{
                                padding: "8px 10px",
                                borderBottom: `1px solid ${T.gray100}`,
                                position: "relative",
                            }}
                        >
                            <div
                                style={{
                                    position: "absolute",
                                    left: 19,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    pointerEvents: "none",
                                }}
                            >
                                <Ico d={IC.search} size={13} color={T.gray400} />
                            </div>
                            <input
                                autoFocus
                                value={q}
                                onChange={e => setQ(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                placeholder="Search brands…"
                                style={{
                                    width: "100%",
                                    boxSizing: "border-box",
                                    background: T.gray50,
                                    border: `1.5px solid ${T.gray200}`,
                                    borderRadius: 6,
                                    padding: "6px 10px 6px 28px",
                                    fontSize: 12,
                                    color: T.gray700,
                                    outline: "none",
                                }}
                            />
                        </div>

                        {/* Options list */}
                        <div style={{ maxHeight: 200, overflowY: "auto" }}>
                            {filtered.length === 0 && (
                                <div
                                    style={{
                                        padding: "12px 14px",
                                        fontSize: 12,
                                        color: T.gray400,
                                        fontStyle: "italic",
                                    }}
                                >
                                    No brands match "{q}"
                                </div>
                            )}
                            {filtered.map((o: MultiSelectOption) => {
                                const checked = value.includes(o.value);
                                const settings = brandSettings?.[o.value] ?? {
                                    technical_support: false,
                                    price_availability: false,
                                };

                                return (
                                    <div key={o.value}>
                                        {/* Brand row */}
                                        <div
                                            onClick={() => toggle(o.value)}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                                padding: "9px 12px",
                                                cursor: "pointer",
                                                fontSize: 13,
                                                color: checked ? T.blue600 : T.gray700,
                                                background: checked ? T.blue50 : "transparent",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: 4,
                                                    background: checked ? T.blue500 : T.white,
                                                    border: `1.5px solid ${checked ? T.blue500 : T.gray300}`,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {checked && <Ico d={IC.check} size={10} color="#fff" sw={3} />}
                                            </div>
                                            {o.label}
                                        </div>

                                        {/* ✅ Show extra options ONLY if selected */}
                                        {checked && (
                                            <div
                                                style={{
                                                    paddingLeft: 32,
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    gap: 6,
                                                    paddingBottom: 6,
                                                    paddingTop: 6,
                                                }}
                                            >
                                                {[
                                                    { key: "technical_support", label: "Technical Support" },
                                                    { key: "price_availability", label: "Price Availability" },
                                                ].map(({ key, label }) => (
                                                    <div
                                                        key={key}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            onUpdateBrandSetting?.(o.value, key, !settings[key]);
                                                        }}
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 8,
                                                            cursor: "pointer",
                                                            fontSize: 12,
                                                            color: T.gray700,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: 14,
                                                                height: 14,
                                                                borderRadius: 3,
                                                                background: settings[key] ? T.blue500 : T.white,
                                                                border: `1.5px solid ${settings[key] ? T.blue500 : T.gray300
                                                                    }`,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            {settings[key] && (
                                                                <Ico d={IC.check} size={8} color="#fff" sw={3} />
                                                            )}
                                                        </div>
                                                        {label}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer summary */}
                        {value.length > 0 && (
                            <div
                                style={{
                                    padding: "7px 12px",
                                    borderTop: `1px solid ${T.gray100}`,
                                    background: T.gray50,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <span style={{ fontSize: 11, color: T.gray500 }}>{value.length} selected</span>
                                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                                        <input
                                            type="checkbox"
                                            checked={showSelectedOnly}
                                            onChange={(e) => setShowSelectedOnly(e.target.checked)}
                                            style={{ cursor: "pointer" }}
                                        />
                                        <span style={{ fontSize: 11, color: T.gray600 }}>Show selected filters</span>
                                    </label>
                                </div>
                                <span
                                    onClick={() => onChange([])}
                                    style={{ fontSize: 11, fontWeight: 600, color: T.red500, cursor: "pointer" }}
                                >
                                    Clear all
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {error && (
                <Typography style={{ display: "block", marginTop: 5, fontSize: 12, color: T.red500 }}>
                    {error}
                </Typography>
            )}
        </Field>
    );
}

interface ToggleProps {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
}
const Toggle: FC<ToggleProps> = ({ label, description, checked, onChange }) => (
    <StyledWrapper
        style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "9px 0",
        }}
    >
        <StyledWrapper>
            <StyledWrapper style={{ fontSize: 13, fontWeight: 600, color: T.gray700, lineHeight: 1.3 }}>
                {label}
            </StyledWrapper>
            {description && (
                <StyledWrapper style={{ fontSize: 11, color: T.gray400, marginTop: 2 }}>{description}</StyledWrapper>
            )}
        </StyledWrapper>
        <button
            type="button"
            onClick={() => onChange(!checked)}
            style={{
                width: 40,
                height: 22,
                borderRadius: 11,
                border: "none",
                background: checked ? T.blue500 : T.gray200,
                flexShrink: 0,
                position: "relative",
                cursor: "pointer",
                transition: "background 0.17s",
                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.08)",
            }}
        >
            <span
                style={{
                    position: "absolute",
                    top: 3,
                    left: checked ? 19 : 3,
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: T.white,
                    transition: "left 0.17s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
                    display: "block",
                }}
            />
        </button>
    </StyledWrapper>
);

// ─── DIVIDER ──────────────────────────────────────────────────────────────────

const Divider: FC<{ label?: string }> = ({ label }) => (
    <StyledWrapper {...styles.dividerWrapper}>
        <StyledWrapper style={{ flex: 1, height: 1, background: T.gray200 }} />
        {label && (
            <span
                style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: T.gray400,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </span>
        )}
        <StyledWrapper style={{ flex: 1, height: 1, background: T.gray200 }} />
    </StyledWrapper>
);

// ─── MODAL ────────────────────────────────────────────────────────────────────

interface ModalProps {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    onClose: () => void;
    onSave?: () => void;
    saveLabel?: string;
    maxWidth?: number;
    disabled?: boolean;
}
const Modal: FC<ModalProps> = ({
    title,
    subtitle,
    children,
    onClose,
    onSave,
    saveLabel = "Save Changes",
    maxWidth = 580,
    disabled = false,
}) => (
    <StyledWrapper {...styles.modalOverlayWrapper}>
        <StyledWrapper {...styles.modalContentWrapper} style={{ maxWidth }}>
            <StyledWrapper {...styles.modalHeaderWrapper}>
                <StyledWrapper>
                    <StyledWrapper
                        style={{ fontSize: 16, fontWeight: 800, color: T.gray900, letterSpacing: "-0.02em" }}
                    >
                        {title}
                    </StyledWrapper>
                    {subtitle && (
                        <StyledWrapper style={{ fontSize: 12, color: T.gray400, marginTop: 3 }}>
                            {subtitle}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                <Button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: T.gray400,
                        padding: 3,
                        borderRadius: 5,
                        lineHeight: 1,
                        minWidth: 26,
                    }}
                >
                    <Ico d={IC.x} size={17} />
                </Button>
            </StyledWrapper>
            <StyledWrapper style={{ padding: "22px 24px", overflowY: "auto", flex: 1 }}>{children}</StyledWrapper>
            <StyledWrapper {...styles.modalFooterWrapper}>
                <Button variant="secondary" onClick={onClose}>
                    {translate("Cancel")}
                </Button>
                {onSave && (
                    <Btn onClick={onSave} icon="save" disabled={disabled}>
                        {translate(saveLabel)}
                    </Btn>
                )}
            </StyledWrapper>
        </StyledWrapper>
    </StyledWrapper>
);

// ─── CONFIRM MODAL ────────────────────────────────────────────────────────────

interface ConfirmModalProps {
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}
const ConfirmModal: FC<ConfirmModalProps> = ({ message, onConfirm, onCancel }) => (
    <StyledWrapper {...styles.confirmModalWrapper}>
        <StyledWrapper
            style={{
                background: T.white,
                borderRadius: 12,
                width: 380,
                padding: 26,
                boxShadow: T.shadowLg,
                border: `1px solid ${T.gray200}`,
            }}
        >
            <StyledWrapper style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 20 }}>
                <StyledWrapper {...styles.confirmModalIconWrapper}>
                    <Ico d={IC.alert} size={17} color={T.red500} />
                </StyledWrapper>
                <StyledWrapper>
                    <StyledWrapper style={{ fontSize: 14, fontWeight: 700, color: T.gray900, marginBottom: 5 }}>
                        {translate("Confirm Deletion")}
                    </StyledWrapper>
                    <StyledWrapper style={{ fontSize: 13, color: T.gray500, lineHeight: 1.55 }}>
                        {message}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper style={{ display: "flex", justifyContent: "flex-end", gap: 9 }}>
                <Btn variant="secondary" onClick={onCancel}>
                    {translate("Cancel")}
                </Btn>
                <Btn variant="danger" icon="trash" onClick={onConfirm}>
                    {translate("Delete")}
                </Btn>
            </StyledWrapper>
        </StyledWrapper>
    </StyledWrapper>
);

// ─── DATA TABLE ───────────────────────────────────────────────────────────────

interface DataTableColumn {
    key: string;
    label: string;
    render?: (value: any, row: any) => React.ReactNode;
}
interface DataTableProps {
    columns: DataTableColumn[];
    rows: any[];
    onEdit: (row: any) => void;
    onDelete: (row: any) => void;
}
const DataTable: FC<DataTableProps> = ({ columns, rows, onEdit, onDelete }) => (
    <StyledWrapper {...styles.tableWrapper}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
                <tr style={{ background: T.gray50, borderBottom: `1px solid ${T.gray200}` }}>
                    {columns.map(c => (
                        <th
                            key={c.key}
                            style={{
                                padding: "10px 16px",
                                textAlign: "left",
                                fontSize: 11,
                                fontWeight: 700,
                                color: T.gray500,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {c.label}
                        </th>
                    ))}
                    <th
                        style={{
                            padding: "10px 16px",
                            textAlign: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            color: T.gray500,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        {translate("Actions")}
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 && (
                    <tr>
                        <td
                            colSpan={columns.length + 1}
                            style={{
                                padding: "44px 16px",
                                textAlign: "center",
                                color: T.gray400,
                                fontSize: 13,
                                fontStyle: "italic",
                            }}
                        >
                            {translate("No records found")}
                        </td>
                    </tr>
                )}
                {rows.map((row, i) => (
                    <tr
                        key={row.id || i}
                        style={{
                            borderBottom: i < rows.length - 1 ? `1px solid ${T.gray100}` : "none",
                            transition: "background 0.1s",
                        }}
                    // onMouseEnter={e => (e.currentTarget.style.background = T.gray50)}
                    // onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                        {columns.map(c => (
                            <td
                                key={c.key}
                                style={{
                                    padding: "13px 16px",
                                    fontSize: 13,
                                    color: T.gray600,
                                    verticalAlign: "middle",
                                }}
                            >
                                {c.render
                                    ? c.render(row[c.key], row)
                                    : row[c.key] || <span style={{ color: T.gray300 }}>—</span>}
                            </td>
                        ))}
                        <td style={{ padding: "13px 16px", textAlign: "right" }}>
                            <StyledWrapper {...styles.actionButtonsWrapper}>
                                <Btn size="sm" variant="secondary" icon="edit" onClick={() => onEdit(row)}>
                                    {translate("Edit")}
                                </Btn>
                                <Btn size="sm" variant="danger" icon="trash" onClick={() => onDelete(row)} />
                            </StyledWrapper>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </StyledWrapper>
);
interface DataTableCustomProps {
    columns: DataTableColumn[];
    rows: any[];
    onRowClick: (row: any) => void;
}
const DataTableCustom: FC<DataTableCustomProps> = ({ columns, rows, onRowClick }) => (
    <StyledWrapper {...styles.tableWrapper}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
                <tr style={{ background: T.gray50, borderBottom: `1px solid ${T.gray200}` }}>
                    {columns.map(c => (
                        <th
                            key={c.key}
                            style={{
                                padding: "10px 16px",
                                textAlign: "left",
                                fontSize: 11,
                                fontWeight: 700,
                                color: T.gray500,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {c.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.length === 0 && (
                    <tr>
                        <td
                            colSpan={columns.length + 1}
                            style={{
                                padding: "44px 16px",
                                textAlign: "center",
                                color: T.gray400,
                                fontSize: 13,
                                fontStyle: "italic",
                            }}
                        >
                            {translate("No records found")}
                        </td>
                    </tr>
                )}
                {rows.map((row, i) => (
                    <tr
                        key={row.id || i}
                        onClick={() => onRowClick?.(row)}
                        style={{
                            borderBottom: i < rows.length - 1 ? `1px solid ${T.gray100}` : "none",
                            transition: "background 0.1s",
                            cursor: "pointer",
                        }}
                    // onMouseEnter={e => (e.currentTarget.style.background = T.gray50)}
                    // onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                    >
                        {columns.map(c => (
                            <td
                                key={c.key}
                                style={{
                                    padding: "13px 16px",
                                    fontSize: 13,
                                    color: T.gray600,
                                    verticalAlign: "middle",
                                }}
                            >
                                {c.render
                                    ? c.render(row[c.key], row)
                                    : row[c.key] || <span style={{ color: T.gray300 }}>—</span>}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </StyledWrapper>
);

// ─── PAGE HEADER ──────────────────────────────────────────────────────────────

interface PageHeaderProps {
    title: string;
    subtitle?: string;
    count?: number;
    onAdd?: () => void;
    addLabel?: string;
    search?: string;
    onSearch?: (v: string) => void;
    searchValue?: string;
    onClearSearch?: () => void;
    showSearch?: boolean;
    onSearchClick?: () => void;
}
const PageHeader: FC<PageHeaderProps> = ({
    title,
    subtitle,
    count,
    onAdd,
    addLabel,
    search,
    onSearch,
    searchValue,
    onClearSearch,
    onSearchClick,
    showSearch,
}) => (
    <StyledWrapper {...styles.pageHeaderWrapper}>
        <StyledWrapper {...styles.tableHeaderWrapper}>
            <StyledWrapper>
                <Typography {...styles.sectionTitle}>{translate(title)}</Typography>
                <Typography {...styles.pageHeaderSubtitle}>
                    {subtitle && translate(subtitle)}
                    {count !== undefined ? ` · ${count} ${translate(count !== 1 ? "records" : "record")}` : ""}
                </Typography>
            </StyledWrapper>
            {onAdd && (
                <Btn icon="plus" size="md" onClick={onAdd}>
                    {translate(addLabel || `New ${title.replace(/s$/, "")}`)}
                </Btn>
            )}
        </StyledWrapper>
        {showSearch && onSearch && (
            <StyledWrapper {...styles.searchBarWrapper}>
                <input
                    value={search}
                    onChange={e => onSearch(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && onSearchClick?.()}
                    placeholder={translate("Search records…")}
                    style={{
                        width: "100%",
                        boxSizing: "border-box",
                        background: T.white,
                        border: `1.5px solid ${T.gray300}`,
                        borderRadius: 7,
                        padding: search?.trim() ? "8px 100px 8px 10px" : "8px 70px 8px 10px",
                        fontSize: 13,
                        color: T.gray700,
                        outline: "none",
                    }}
                />

                {search?.trim() && onClearSearch && (
                    <StyledWrapper
                        style={{
                            position: "absolute",
                            right: 38,
                            top: "50%",
                            transform: "translateY(-50%)",
                        }}
                    >
                        <button
                            type="button"
                            title={translate("Clear search")}
                            onClick={() => {
                                onSearch("");
                                onClearSearch();
                            }}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 26,
                                height: 26,
                                borderRadius: 5,
                                border: `1px solid ${T.gray200}`,
                                background: T.gray100,
                                cursor: "pointer",
                                color: T.gray500,
                                padding: 0,
                            }}
                        >
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </StyledWrapper>
                )}

                {onSearchClick && (
                    <StyledWrapper
                        style={{
                            position: "absolute",
                            right: 6,
                            top: "50%",
                            transform: "translateY(-50%)",
                        }}
                        {...styles.customCss}
                    >
                        <Btn size="sm" onClick={onSearchClick}>
                            <Ico d={IC.search} size={15} color={T.gray900} />
                        </Btn>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        )}
    </StyledWrapper>
);

// ─── AVATAR ───────────────────────────────────────────────────────────────────

const Avatar: FC<{ first: string; last: string }> = ({ first, last }) => (
    <StyledWrapper
        style={{
            width: 33,
            height: 33,
            borderRadius: "50%",
            background: T.blue50,
            border: `1.5px solid ${T.blue100}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11.5,
            fontWeight: 700,
            color: T.blue600,
            flexShrink: 0,
        }}
    >
        {first[0]}
        {last[0]}
    </StyledWrapper>
);

// ─── EMPLOYEES PAGE ───────────────────────────────────────────────────────────

interface Branch {
    id: string;
    name: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    zip_code: string;
    is_deleted: boolean;
}

const isAuthError = (error: string): boolean => {
    const lower = (error ?? "").toLowerCase();
    return lower.includes("401") || lower.includes("authorization has been denied") || lower.includes("unauthorized");
};

const SessionExpiredBanner = ({ onRetry, signInUrl }: { onRetry?: () => void; signInUrl?: string }) => (
    <StyledWrapper
        style={{
            background: "#fffbeb",
            border: `1px solid #fcd34d`,
            borderRadius: 8,
            padding: "20px 24px",
            fontSize: 13,
            color: "#92400e",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
        }}
    >
        <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <StyledWrapper>
                <StyledWrapper style={{ fontWeight: 700, marginBottom: 2 }}>
                    {translate("Your session has expired")}
                </StyledWrapper>
                <StyledWrapper style={{ fontSize: 12, color: "#b45309" }}>
                    {translate("Please Login again to continue.")}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
        <Btn
            variant="secondary"
            size="sm"
            onClick={() => {
                window.location.href = signInUrl || "/";
            }}
        >
            {translate("Login")}
        </Btn>
    </StyledWrapper>
);

const EmployeesPage: FC<EmployeesPageProps> = ({
    showSearch,
    addEmployeeData,
    getEmployeeCollectionData,
    updateEmployeeData,
    deleteEmployeeData,
    getBranchCollectionData,
    getDivisionCollectionData,
    getProductTypeCollectionData,
    getBrandsCollectionData,
    getEmployeesById,
    signInUrl,
}) => {
    const [employees, setEmployees] = useState<any[]>([]);
    const [employeesPagination, setEmployeesPagination] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);
    const [productTypes, setProductTypes] = useState<any[]>([]);
    const [brands, setBrands] = useState<any[]>([]); // setProducts
    const [loadError, setLoadError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState<string | null>(null);
    const [form, setForm] = useState<any>({});
    const [del, setDel] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [maxLengthErrors, setMaxLengthErrors] = useState<Record<string, string>>({});
    const [branches, setBranches] = useState<any[]>([]);
    const [divisions, setDivisions] = useState<any[]>([]);

    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    //     const [photoFile, setPhotoFile] = useState<File | null>(null);
    // const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string>("");
    // const [photoError, setPhotoError] = useState<string>("");
    // const photoUploadRef = React.useRef({ value: "" } as HTMLInputElement);
    React.useEffect(() => {
        const handler = () => setOpenDropdown(null);
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string>("");
    const [photoError, setPhotoError] = useState<string>("");
    const photoUploadRef = React.useRef({ value: "" } as HTMLInputElement);
    const formRef = React.useRef<HTMLDivElement>(null);
    const origin = "userfiles";
    const [refreshCount, setRefreshCount] = useState(0);

    // ── Bulk Modal States ──
    const [bulkBrandModal, setBulkBrandModal] = useState(false);
    const [bulkBrandText, setBulkBrandText] = useState("");
    const [bulkProductTypeModal, setBulkProductTypeModal] = useState(false);
    const [bulkProductTypeText, setBulkProductTypeText] = useState("");

    React.useEffect(() => {
        getBranchCollectionData({
            pageSize: 3000,
            onSuccess: (result: BranchCollectionModel) => {
                setBranches(
                    (result.branchModels ?? []).map(b => ({
                        id: b.id,
                        name: b.name,
                        is_deleted: false,
                    })),
                );
            },
            onError: () => { }, // silent — branches dropdown will just be empty
        });
    }, []);
    // Removed test useEffect for getEmployeesById("123rh")

    React.useEffect(() => {
        getDivisionCollectionData({
            pageSize: 3000,
            onSuccess: (result: DivisionCollectionModel) => {
                setDivisions(
                    (result.divisionModels ?? []).map(d => ({
                        id: d.id,
                        name: d.name,
                    })),
                );
            },
            onError: () => { },
        });
    }, []);
    React.useEffect(() => {
        getProductTypeCollectionData({
            pageSize: 3000,
            onSuccess: (result: ProductTypeCollectionModel) => {
                setProductTypes(
                    (result.productTypeModels ?? []).map(d => ({
                        id: d.id,
                        name: d.name,
                    })),
                );
            },

            onError: () => { },
        });
    }, []);
    React.useEffect(() => {
        getBrandsCollectionData({
            pageSize: 3000,
            onSuccess: (result: BrandsCollectionModel) => {
                const brandData = (result.brandsList ?? []).map(d => ({
                    id: d.id,
                    name: d.name,
                }));
                setBrands(brandData);
            },

            onError: error => { },
        });
    }, []);

    const blank = {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        title: "",
        branch_id: "",
        start_date: "",
        bio_snippet: "",
        bio_full: "",
        education: "",
        trainings_attended: "",
        personal_interests: "",
        is_sme: false,
        is_dsm: false,
        is_active: true,
        is_textmessage_allowed: false,
        division_id: "",
        brand_ids: [],
        brand_settings: {} as Record<string, { technical_support: boolean; price_availability: boolean }>,
    };

    // ── Map API → local ────────────────────────────────────────────────
    const mapToLocal = (e: EmployeeModel) => ({
        id: e.id,
        first_name: e.firstName,
        last_name: e.lastName,
        email: e.email,
        phone: e.phone,
        title: e.title,
        branch_id: e.branchId,
        start_date: e.startDate ? e.startDate.split("T")[0] : "",

        bio_snippet: e.bioSnippet ?? "",
        bio_full: e.bioFull ?? "",
        education: e.education ?? "",
        trainings_attended: e.trainingsAttended ?? "",
        personal_interests: e.personalInterests ?? "",

        is_sme: e.isSme,
        is_dsm: e.isDsm,
        is_active: e.isActive,
        is_textmessage_allowed: e.isTextMessageAllowed,

        photo_url: e.photoUrl ?? "",
        division_id: e.divisionId ?? "",
        is_deleted: false,

        // ✅ CORRECT (small b, small i)
        brand_ids: e.brandIds?.map(b => b.brandId) ?? [],

        brand_settings: Object.fromEntries(
            (e.brandIds ?? []).map(b => [
                b.brandId,
                {
                    technical_support: b.isTechnicalSupport ?? false,
                    price_availability: b.isPriceAndAvailability ?? false,
                },
            ]),
        ),

        // ✅ CORRECT
        product_type_ids: e.productTypeIds?.map(pt => pt.productTypeId) ?? [],

        product_settings: Object.fromEntries(
            (e.productTypeIds ?? []).map(pt => [
                pt.productTypeId,
                {
                    technical_support: pt.isTechnicalSupport ?? false,
                    price_availability: pt.isPriceAndAvailability ?? false,
                },
            ]),
        ),
    });

    // ── Load on mount ──────────────────────────────────────────────────
    React.useEffect(() => {
        setIsLoading(true);
        getEmployeeCollectionData({
            page: 1,

            onSuccess: (result: EmployeeCollectionModel) => {
                setEmployees((result.employeeModels ?? []).map(mapToLocal));
                setEmployeesPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    }, [refreshCount]);
    const changePageHandler = (newPageIndex: number) => {
        getEmployeeCollectionData({
            page: newPageIndex,
            pageSize: employeesPagination.pageSize,
            onSuccess: (result: EmployeeCollectionModel) => {
                setEmployees((result.employeeModels ?? []).map(mapToLocal));
                setEmployeesPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };
    const changeResultsPerPageHandler = (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        getEmployeeCollectionData({
            page: 1,
            pageSize: newPageSize,
            query: search,
            onSuccess: (result: EmployeeCollectionModel) => {
                setEmployees((result.employeeModels ?? []).map(mapToLocal));
                setEmployeesPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };

    const handleSearch = () => {
        setIsLoading(true);
        setLoadError(null);
        getEmployeeCollectionData({
            page: 1,
            pageSize: employeesPagination?.pageSize || 50,
            query: search.trim(),
            onSuccess: (result: EmployeeCollectionModel) => {
                setEmployees((result.employeeModels ?? []).map(mapToLocal));
                setEmployeesPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };

    const active = employees.filter(e => !e.is_deleted);

    const filtered = active;

    const f = (k: string) => (v: any) => {
        setForm((p: any) => ({ ...p, [k]: v }));
        // clear field error on change
        if (formErrors[k]) {
            setFormErrors(p => {
                const n = { ...p };
                delete n[k];
                return n;
            });
        }
    };

    // Field handler with maxLength validation
    const fWithMaxLength = (k: string, maxLength: number) => (v: any) => {
        let error = "";
        if (v && v.length > maxLength) {
            error = `Maximum ${maxLength} characters allowed`;
        }

        setForm((p: any) => ({ ...p, [k]: v }));

        if (error) {
            setMaxLengthErrors(p => ({ ...p, [k]: error }));
        } else if (maxLengthErrors[k]) {
            setMaxLengthErrors(p => {
                const n = { ...p };
                delete n[k];
                return n;
            });
        }
    };

    // ── Validation ─────────────────────────────────────────────────────
    const validate = () => {
        const errors: Record<string, string> = {};
        if (!form.first_name?.trim()) {
            errors.first_name = "First name is required";
        } else if (form.first_name.length > 100) {
            errors.first_name = "Maximum 100 characters allowed";
        }
        if (!form.last_name?.trim()) errors.last_name = "Last name is required";

        if (!form.email?.trim()) {
            errors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errors.email = "Enter a valid email";
        } else {
            const emailExists = active.some(
                e => e.email.toLowerCase() === form.email.trim().toLowerCase() && e.id !== form.id,
            );
            if (emailExists) errors.email = "An employee with this email already exists";
        }

        if (!form.phone?.trim()) {
            errors.phone = "Phone is required";
        } else {
            const digitsOnly = form.phone.trim().replace(/\D/g, "");
            if (digitsOnly.length !== 10) {
                errors.phone = "Enter a valid 10-digit phone number";
            } else if (digitsOnly[0] === "0" || digitsOnly[0] === "1") {
                errors.phone = "Phone number cannot start with 0 or 1";
            } else {
                const phoneExists = active.some(e => e.phone.replace(/\D/g, "") === digitsOnly && e.id !== form.id);
                if (phoneExists) errors.phone = "An employee with this phone number already exists";
            }
        }

        if (!form.title?.trim()) errors.title = "Job title is required";
        if (!form.branch_id) errors.branch_id = "Branch is required";
        if (!form.start_date) errors.start_date = "Start date is required";
        // if (!form.bio_full?.trim()) errors.bio_full = "Full bio is required";
        // if (!form.education?.trim()) errors.education = "Education is required";
        // if (!form.trainings_attended?.trim()) errors.trainings_attended = "Trainings attended is required";
        // if (!form.personal_interests?.trim()) errors.personal_interests = "Personal interests is required";

        if (!!form.is_dsm && !form.division_id) {
            errors.division_id = "Division is required for DSM employees";
        }

        if (!!form.is_dsm && !form.is_sme) {
            errors.is_sme = "Employee must be an SME to be assigned as DSM";
        }

        return errors;
    };
    // photo functions
    const validatePhoto = (file: File | null) => {
        if (!file) {
            // Photo is optional — no error if not provided
            setPhotoError("");
            return true;
        }
        const allowedExtensions = ["jpg", "jpeg", "png", "webp"];
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        if (!allowedExtensions.includes(ext)) {
            setPhotoError("Photo must be .jpg, .jpeg, .png, or .webp format");
            return false;
        }
        const maxSizeMB = 5;
        if (file.size > maxSizeMB * 1024 * 1024) {
            setPhotoError(`Photo must be smaller than ${maxSizeMB}MB`);
            return false;
        }
        setPhotoError("");
        return true;
    };

    const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        if (file) {
            const isValid = validatePhoto(file);
            if (isValid) {
                setPhotoFile(file);
                // ── Generate preview URL ──
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPhotoPreviewUrl(reader.result as string);
                };
                reader.readAsDataURL(file);
            } else {
                setPhotoFile(null);
                setPhotoPreviewUrl("");
            }
        } else {
            setPhotoFile(null);
            setPhotoPreviewUrl("");
            setPhotoError("");
        }
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreviewUrl("");
        setPhotoError("");
        if (photoUploadRef.current) {
            // ← guard
            photoUploadRef.current.value = "";
        }
    };

    const getPhotoBase64 = (): Promise<string> => {
        return new Promise(resolve => {
            if (!photoFile) {
                resolve("");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                // ── Return only the base64 part after the comma ──
                const result = reader.result as string;
                const base64 = result.split(",")[1] ?? "";
                resolve(base64);
            };
            reader.readAsDataURL(photoFile);
        });
    };

    const scrollToFirstError = (errors: Record<string, string>) => {
        const firstErrorKey = Object.keys(errors)[0];
        if (!firstErrorKey) return;
        setTimeout(() => {
            const el =
                document.querySelector(`[data-field-id="${firstErrorKey}"]`) ??
                document.querySelector(`[data-error-field="${firstErrorKey}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }, 50);
    };

    // ── Bulk Brand Handler ──
    const handleAddBulkBrands = () => {
        if (!bulkBrandText.trim()) return;

        const inputItems = bulkBrandText
            .split(",")
            .map(item => item.trim())
            .filter(item => item.length > 0);

        const matchedBrandIds = inputItems
            .map(inputName => {
                const matchedBrand = brands.find(
                    b =>
                        !b.is_deleted &&
                        b.name.toLowerCase() === inputName.toLowerCase()
                );
                return matchedBrand?.id;
            })
            .filter((id): id is string => !!id);

        const updatedBrandIds = Array.from(
            new Set([...(form.brand_ids || []), ...matchedBrandIds])
        );

        f("brand_ids")(updatedBrandIds);
        setBulkBrandText("");
        setBulkBrandModal(false);
    };

    // ── Bulk Product Type Handler ──
    const handleAddBulkProductTypes = () => {
        if (!bulkProductTypeText.trim()) return;

        const inputItems = bulkProductTypeText
            .split(",")
            .map(item => item.trim())
            .filter(item => item.length > 0);

        const matchedProductTypeIds = inputItems
            .map(inputName => {
                const matchedProductType = productTypes.find(
                    pt =>
                        !pt.is_deleted &&
                        pt.name.toLowerCase() === inputName.toLowerCase()
                );
                return matchedProductType?.id;
            })
            .filter((id): id is string => !!id);

        const updatedProductTypeIds = Array.from(
            new Set([...(form.product_type_ids || []), ...matchedProductTypeIds])
        );

        f("product_type_ids")(updatedProductTypeIds);
        setBulkProductTypeText("");
        setBulkProductTypeModal(false);
    };

    // ── Save ───────────────────────────────────────────────────────────
    const save = async () => {
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            scrollToFirstError(errors);
            return;
        }
        setSaveError(null);
        setFormErrors({});
        setIsSaving(true);
        const photoBase64 = await getPhotoBase64();

        const apiModel = {
            firstName: form.first_name,
            lastName: form.last_name,
            email: form.email,
            phone: form.phone,
            title: form.title,
            branchId: form.branch_id,
            BrandIds: (form.brand_ids ?? []).map((id: string) => ({
                BrandId: id,
                IsTechnicalSupport: form.brand_settings?.[id]?.technical_support ?? false,
                IsPriceAndAvailability: form.brand_settings?.[id]?.price_availability ?? false,
            })),
            ProductTypeIds: form.is_sme
                ? (form.product_type_ids ?? []).map((id: string) => ({
                    productTypeId: id,
                    isTechnicalSupport: form.product_settings?.[id]?.technical_support ?? false,
                    isPriceAndAvailability: form.product_settings?.[id]?.price_availability ?? false,
                }))
                : [],
            // branchName: branches.find((b: any) => b.id === form.branch_id)?.name ?? "", // ← add this
            startDate: form.start_date,
            bioSnippet: form.bio_snippet || "",
            bioFull: form.bio_full || "",
            education: form.education,
            trainingsAttended: form.trainings_attended,
            personalInterests: form.personal_interests,
            isSme: !!form.is_sme,
            isDsm: !!form.is_dsm,
            isActive: !!form.is_active,
            isTextMessageAllowed: !!form.is_textmessage_allowed,
            ...(!!form.is_sme && form.brandsid ? { brandsId: form.division_id } : {}),
            ImageBase64: photoBase64
                ? `data:image/${photoFile?.name.split(".").pop()?.toLowerCase()};base64,${photoBase64}`
                : "",
            ...(!!form.is_dsm && form.division_id ? { divisionId: form.division_id } : {}),
        };

        if (modal === "create") {
            addEmployeeData({
                model: apiModel,
                onSuccess: (result: EmployeeModel) => {
                    setEmployees(p => [...p, mapToLocal(result)]);
                    setIsSaving(false);
                    setModal(null);
                },
                onError: (error: string) => {
                    setIsSaving(false);
                    const lower: any = (error ?? "").toLowerCase();
                    if (isAuthError(error)) {
                        setSaveError("__AUTH__");
                        return;
                    }
                    if (lower.includes("email")) {
                        const errors = { email: "An employee with this email already exists" };
                        setFormErrors(p => ({ ...p, ...errors }));
                        scrollToFirstError(errors);
                    } else if (lower.includes("phone")) {
                        const errors = { phone: "An employee with this phone number already exists" };
                        setFormErrors(p => ({ ...p, ...errors }));
                        scrollToFirstError(errors);
                        // } else if (lower.errorMessage?.includes("sme") || lower.errorMessage?.includes("dsm")) {
                        //     // silent
                    } else {
                        setSaveError(lower.errorMessage ?? "Something went wrong. Please try again.");
                        scrollToFirstError(errors);
                    }
                },
            });
        } else {
            updateEmployeeData({
                model: { ...apiModel, id: form.id },
                onSuccess: (result: EmployeeModel) => {
                    setEmployees(p => p.map(e => (e.id === result.id ? mapToLocal(result) : e)));
                    setIsSaving(false);
                    setModal(null);
                },
                onError: (error: string) => {
                    setIsSaving(false);
                    const lower: any = (error ?? "").toLowerCase();
                    console.log("Add employee error:", error);
                    console.log("Add employee lower:", lower.errorMessage);
                    if (isAuthError(error)) {
                        setSaveError("__AUTH__");
                        return;
                    }
                    if (lower.includes("email")) {
                        const errors = { email: "An employee with this email already exists" };
                        setFormErrors(p => ({ ...p, ...errors }));
                        scrollToFirstError(errors); // ← ADD THIS
                    } else if (lower.includes("phone")) {
                        const errors = { phone: "An employee with this phone number already exists" };
                        setFormErrors(p => ({ ...p, ...errors }));
                        scrollToFirstError(errors); // ← ADD THIS
                        // } else if (lower.errorMessage?.includes("sme") || lower.errorMessage?.includes("dsm")) {
                        //     // silent
                    } else {
                        setSaveError(lower.errorMessage ?? "Something went wrong. Please try again.");
                        scrollToFirstError(errors); // ← ADD THIS
                    }
                },
            });
        }
    };

    // ── Loading ────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <StyledWrapper
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "80px 0",
                    color: T.gray400,
                    fontSize: 13,
                    gap: 10,
                }}
            >
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.gray300}
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ animation: "spin 1s linear infinite" }}
                >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                {translate("Loading employees…")}
            </StyledWrapper>
        );
    }

    // ── Load error ─────────────────────────────────────────────────────
    if (loadError) {
        return isAuthError(loadError) ? (
            <SessionExpiredBanner signInUrl={signInUrl} />
        ) : (
            <StyledWrapper
                style={{
                    background: T.red50,
                    border: `1px solid #fecaca`,
                    borderRadius: 8,
                    padding: "16px 20px",
                    fontSize: 13,
                    color: T.red600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                }}
            >
                <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Ico d={IC.alert} size={15} color={T.red500} />
                    {translate("Failed to load employees: ")} {loadError}
                </StyledWrapper>
                <Btn
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        setIsLoading(true);
                        setLoadError(null);
                        getEmployeeCollectionData({
                            onSuccess: (result: EmployeeCollectionModel) => {
                                setEmployees((result.employeeModels ?? []).map(mapToLocal));
                                setIsLoading(false);
                            },
                            onError: (error: string) => {
                                setLoadError(error);
                                setIsLoading(false);
                            },
                        });
                    }}
                >
                    {translate("Retry")}
                </Btn>
            </StyledWrapper>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────
    return (
        <StyledWrapper data-test-selector="adminEmployeesPage">
            <PageHeader
                title="Employees"
                subtitle="Manage employee profiles, roles, and assignments"
                count={employeesPagination.totalItemCount}
                search={search}
                // BEFORE
                // onSearch={setSearch}
                // AFTER
                onSearch={v => {
                    setSearch(v);
                    if (v === "") {
                        setIsLoading(true);
                        getEmployeeCollectionData({
                            page: 1,
                            pageSize: employeesPagination?.pageSize || 50,
                            query: "",
                            onSuccess: result => {
                                setEmployees((result.employeeModels ?? []).map(mapToLocal));
                                setEmployeesPagination(result.pagination);
                                setIsLoading(false);
                            },
                            onError: error => {
                                setLoadError(error);
                                setIsLoading(false);
                            },
                        });
                    }
                }}
                onSearchClick={handleSearch}
                showSearch={true}
                addLabel="New Employee"
                onAdd={() => {
                    setForm({ ...blank });
                    setSaveError(null);
                    setFormErrors({});
                    setPhotoFile(null);
                    setPhotoPreviewUrl("");
                    setPhotoError("");
                    setModal("create");
                }}
                onClearSearch={() => {
                    setSearch("");
                    getEmployeeCollectionData({
                        page: 1,
                        pageSize: employeesPagination?.pageSize || 50,
                        query: "",
                        onSuccess: result => {
                            setEmployees((result.employeeModels ?? []).map(mapToLocal));
                            setEmployeesPagination(result.pagination);
                        },
                        onError: error => setLoadError(error),
                    });
                }}
            />

            {saveError && !modal && (
                <StyledWrapper
                    style={{
                        background: T.red50,
                        border: `1px solid #fecaca`,
                        borderRadius: 7,
                        padding: "10px 14px",
                        marginBottom: 16,
                        fontSize: 13,
                        color: T.red600,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <Ico d={IC.alert} size={14} color={T.red500} />
                    {saveError}
                </StyledWrapper>
            )}

            <StyledWrapper
                style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch", borderRadius: 8 }}
            >
                <StyledWrapper {...styles.employeWrapper}>
                    <DataTable
                        columns={[
                            // {
                            //     key: "name",
                            //     label: translate("Employee"),
                            //     render: (_: any, r: any) => (
                            //         <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            //             <Avatar first={r.first_name || "?"} last={r.last_name || "?"} />
                            //             <StyledWrapper>
                            //                 <StyledWrapper style={{ fontWeight: 700, color: T.gray900, fontSize: 13 }}>
                            //                     {r.first_name} {r.last_name}
                            //                 </StyledWrapper>
                            //                 <StyledWrapper style={{ fontSize: 11, color: T.gray400 }}>
                            //                     {r.email}
                            //                 </StyledWrapper>
                            //             </StyledWrapper>
                            //         </StyledWrapper>
                            //     ),
                            // },
                            {
                                key: "name",
                                label: translate("Employee"),
                                render: (_: any, r: any) => {
                                    const hasValidPhoto =
                                        r.photo_url &&
                                        /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif|ico|tiff|tif|jfif)$/i.test(r.photo_url);
                                    return (
                                        <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            {hasValidPhoto ? (
                                                <img
                                                    src={`${origin}/${r.photo_url}`}
                                                    alt={`${r.first_name} ${r.last_name}`}
                                                    style={{
                                                        width: 33,
                                                        height: 33,
                                                        borderRadius: "50%",
                                                        objectFit: "cover",
                                                        border: `1.5px solid ${T.blue100}`,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            ) : (
                                                <AvatarPlaceholder size={33} />
                                            )}
                                            <StyledWrapper>
                                                <StyledWrapper style={{ fontWeight: 700, color: T.gray900, fontSize: 13 }}>
                                                    {r.first_name} {r.last_name}
                                                </StyledWrapper>
                                                <StyledWrapper style={{ fontSize: 11, color: T.gray400 }}>
                                                    {r.email}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    )
                                },
                            },
                            { key: "title", label: translate("Title") },
                            {
                                key: "branch_id",
                                label: translate("Branch"),
                                render: (v: string, r: any) =>
                                    r.branch_name || branches.find((b: any) => b.id === v)?.name || "—",
                            },
                            { key: "phone", label: translate("Phone") },
                            {
                                key: "roles",
                                label: translate("Roles"),
                                render: (_: any, r: any) => (
                                    <StyledWrapper style={{ display: "flex", gap: 4 }}>
                                        {r.is_sme && <Badge variant="blue">SME</Badge>}
                                        {r.is_dsm && <Badge variant="amber">DSM</Badge>}
                                        {!r.is_sme && !r.is_dsm && (
                                            <span style={{ fontSize: 12, color: T.gray300 }}>—</span>
                                        )}
                                    </StyledWrapper>
                                ),
                            },
                            {
                                key: "is_active",
                                label: translate("Status"),
                                render: (v: boolean) =>
                                    v ? (
                                        <Badge variant="green">{translate("Active")}</Badge>
                                    ) : (
                                        <Badge variant="ghost">{translate("Inactive")}</Badge>
                                    ),
                            },
                        ]}
                        rows={filtered}
                        onEdit={async e => {
                            try {
                                // ── Reset photo state FIRST — but DON'T touch the ref (input not in DOM yet) ──
                                setPhotoFile(null);
                                setPhotoPreviewUrl("");
                                setPhotoError("");
                                // ← photoUploadRef.current.value = ""; REMOVED — input not mounted yet

                                await new Promise((resolve, reject) => {
                                    getEmployeesById({
                                        id: e.id,
                                        onSuccess: data => {
                                            const mapped = mapToLocal(data);
                                            setForm(mapped);
                                            // ── Initialize preview from existing photo_url ──
                                            if (mapped.photo_url) {
                                                setPhotoPreviewUrl(`${origin}/${mapped.photo_url}`);
                                            } else {
                                                setPhotoPreviewUrl("");
                                            }

                                            resolve(data);
                                        },
                                        onError: error => reject(new Error(error)),
                                    });
                                });

                                setSaveError(null);
                                setFormErrors({});
                                setModal("edit");
                            } catch (error: any) {
                                setSaveError(`Failed to load employee: ${error.message}`);
                            }
                        }}
                        onDelete={e => setDel(e)}
                    />
                </StyledWrapper>
            </StyledWrapper>

            {modal && (
                <Modal
                    title={modal === "create" ? translate("New Employee") : translate("Edit Employee")}
                    subtitle={
                        modal === "edit"
                            ? `${form.first_name} ${form.last_name} · ${form.title}`
                            : translate("Complete all required fields to create the employee record")
                    }
                    onClose={() => {
                        setModal(null);
                        setSaveError(null);
                        setFormErrors({});
                        setMaxLengthErrors({});
                        setPhotoFile(null);
                        setPhotoPreviewUrl("");
                        setPhotoError("");
                        if (photoUploadRef.current) {
                            // ← guard
                            photoUploadRef.current.value = "";
                        }
                    }}
                    onSave={save}
                    saveLabel={isSaving ? "Saving…" : modal === "create" ? "Create Employee" : "Save Changes"}
                    maxWidth={660}
                    disabled={Object.keys(maxLengthErrors).length > 0}
                >
                    {saveError && (
                        <StyledWrapper
                            style={{
                                background: saveError === "__AUTH__" ? "#fffbeb" : T.red50,
                                border: `1px solid ${saveError === "__AUTH__" ? "#fcd34d" : "#fecaca"}`,
                                borderRadius: 7,
                                padding: "10px 14px",
                                marginBottom: 16,
                                fontSize: 13,
                                color: saveError === "__AUTH__" ? "#92400e" : T.red600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                            }}
                        >
                            <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Ico d={IC.alert} size={14} color={saveError === "__AUTH__" ? "#d97706" : T.red500} />
                                {saveError === "__AUTH__"
                                    ? translate("Your session has expired. Please Login again to continue.")
                                    : saveError}
                            </StyledWrapper>
                            {saveError === "__AUTH__" && (
                                <Btn
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        window.location.href = signInUrl || "/";
                                    }}
                                >
                                    {translate("Login")}
                                </Btn>
                            )}
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.formGridContainer}>
                        <GridItem {...styles.formGridItem} data-field-id="first_name">
                            <Input
                                label={translate("First Name")}
                                value={form.first_name}
                                onChange={fWithMaxLength("first_name", 100)}
                                required
                                disabled={isSaving}
                                error={formErrors.first_name || maxLengthErrors.first_name}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="last_name">
                            <Input
                                label={translate("Last Name")}
                                value={form.last_name}
                                onChange={fWithMaxLength("last_name", 100)}
                                required
                                disabled={isSaving}
                                error={formErrors.last_name || maxLengthErrors.last_name}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="email">
                            <Input
                                label={translate("Email")}
                                type="email"
                                value={form.email}
                                onChange={f("email")}
                                required
                                disabled={isSaving}
                                error={formErrors.email}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="phone">
                            <Input
                                label={translate("Phone")}
                                value={form.phone}
                                onChange={v => {
                                    const digits = v.replace(/\D/g, "").slice(0, 10);
                                    let formatted = digits;
                                    if (digits.length >= 7) {
                                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                                    } else if (digits.length >= 4) {
                                        formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                                    } else if (digits.length >= 1) {
                                        formatted = `(${digits}`;
                                    }
                                    f("phone")(formatted);
                                }}
                                placeholder="(XXX) XXX-XXXX"
                                required
                                disabled={isSaving}
                                error={formErrors.phone}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="title">
                            <Input
                                label={translate("Job Title")}
                                value={form.title}
                                onChange={fWithMaxLength("title", 100)}
                                required
                                disabled={isSaving}
                                error={formErrors.title || maxLengthErrors.title}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="branch_id">
                            <SearchableSelect
                                label={translate("Branch")}
                                value={form.branch_id ?? ""}
                                onChange={v => {
                                    f("branch_id")(v);
                                    if (v) setFormErrors(p => ({ ...p, branch_id: "" }));
                                }}
                                required
                                error={formErrors.branch_id}
                                options={branches
                                    .filter((b: any) => !b.is_deleted)
                                    .map((b: any) => ({ value: b.id, label: b.name }))}
                                placeholder="Search branch…"
                                dropdownId="branch" // ← add
                                openDropdown={openDropdown} // ← add
                                onOpenDropdown={setOpenDropdown}
                            />
                            {/* <Field label={translate("Branch")} required error={formErrors.branch_id}>
                                <select
                                    value={form.branch_id ?? ""}
                                    onChange={e => {
                                        f("branch_id")(e.target.value);
                                    }}
                                    style={{
                                        background: T.white,
                                        border: `1.5px solid ${formErrors.branch_id ? T.red500 : T.gray300}`,
                                        borderRadius: 7,
                                        padding: "9px 11px",
                                        color: form.branch_id ? T.gray800 : T.gray400,
                                        fontSize: 13,
                                        outline: "none",
                                        width: "100%",
                                        boxSizing: "border-box",
                                        boxShadow: formErrors.branch_id ? `0 0 0 3px rgba(239,68,68,0.15)` : "none",
                                        transition: "all 0.13s",
                                    }}
                                >
                                    <option value="">— {translate("Select")} —</option>
                                    {branches
                                        .filter((b: any) => !b.is_deleted)
                                        .map((b: any) => (
                                            <option key={b.id} value={b.id}>
                                                {b.name}
                                            </option>
                                        ))}
                                </select>
                            </Field> */}
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="start_date">
                            <Input
                                label={translate("Start Date")}
                                type="date"
                                value={form.start_date}
                                onChange={f("start_date")}
                                required
                                disabled={isSaving}
                                error={formErrors.start_date}
                            />
                        </GridItem>
                    </GridContainer>
                    {/* ── Photo Upload ── */}
                    <GridContainer {...styles.formGridContainer}>
                        <GridItem width={12}>
                            <Field
                                label={translate("Profile Photo")}
                                hint={translate("Optional · JPG, PNG, WEBP · Max 5MB")}
                                error={photoError}
                            >
                                <StyledWrapper
                                    style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}
                                >
                                    {photoPreviewUrl && /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif|ico|tiff|tif|jfif)$/i.test(photoPreviewUrl) ? (
                                        <StyledWrapper style={{ position: "relative", flexShrink: 0 }}>
                                            <img
                                                src={
                                                    photoPreviewUrl.startsWith("data:")
                                                        ? photoPreviewUrl
                                                        : photoPreviewUrl.startsWith(origin)
                                                            ? photoPreviewUrl
                                                            : `${origin}/${photoPreviewUrl}`
                                                }
                                                alt="Preview"
                                                style={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: "50%",
                                                    objectFit: "cover",
                                                    border: `2px solid ${T.gray200}`,
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={removePhoto}
                                                style={{
                                                    position: "absolute",
                                                    top: -4,
                                                    right: -4,
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    background: T.red500,
                                                    border: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#fff",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                ×
                                            </button>
                                        </StyledWrapper>
                                    ) : (
                                        <StyledWrapper
                                            style={{
                                                width: 72,
                                                height: 72,
                                                borderRadius: "50%",
                                                background: T.gray100,
                                                border: `2px dashed ${photoError ? T.red500 : T.gray300}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke={T.gray400}
                                                strokeWidth="1.5"
                                            >
                                                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                                            </svg>
                                        </StyledWrapper>
                                    )}
                                    <StyledWrapper style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        <label
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                padding: "7px 14px",
                                                borderRadius: 7,
                                                border: `1.5px solid ${photoError ? T.red500 : T.gray300}`,
                                                background: T.white,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: T.gray600,
                                                cursor: "pointer",
                                            }}
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                                            </svg>
                                            {photoFile ? translate("Change Photo") : translate("Upload Photo")}
                                            <input
                                                ref={photoUploadRef}
                                                type="file"
                                                accept=".jpg,.jpeg,.png,.webp,.JPG,.JPEG,.PNG,.WEBP"
                                                style={{ display: "none" }}
                                                onChange={handlePhotoChange}
                                                disabled={isSaving}
                                            />
                                        </label>
                                        {photoFile && (
                                            <span style={{ fontSize: 11, color: T.gray400 }}>
                                                {photoFile.name} ({(photoFile.size / 1024).toFixed(0)}KB)
                                            </span>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </Field>
                        </GridItem>
                    </GridContainer>

                    <Divider label={translate("Bio & Background")} />
                    <GridContainer {...styles.formGridContainer}>
                        <GridItem width={12}>
                            <Input
                                label={translate("Bio Snippet")}
                                value={form.bio_snippet}
                                onChange={fWithMaxLength("bio_snippet", 255)}
                                placeholder={translate("Short summary (max 255 chars)")}
                                hint={translate("Shown in search results and staff directory cards")}
                                disabled={isSaving}
                                error={formErrors.bio_snippet || maxLengthErrors.bio_snippet}
                            />
                        </GridItem>
                        <GridItem width={12} data-field-id="bio_full">
                            <StyledWrapper
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    width: "100%",
                                }}
                            >
                                <StyledWrapper
                                    style={{
                                        position: "relative",
                                    }}
                                    css={
                                        formErrors.bio_full
                                            ? css`
                                                  textarea {
                                                      border-color: ${T.red500} !important;
                                                  }
                                              `
                                            : undefined
                                    }
                                >
                                    <TextareaInput
                                        label={translate("Full Bio")}
                                        value={form.bio_full}
                                        onChange={v => {
                                            f("bio_full")(v);
                                            if (formErrors.bio_full) {
                                                setFormErrors(p => {
                                                    const n = { ...p };
                                                    delete n.bio_full;
                                                    return n;
                                                });
                                            }
                                        }}
                                        placeholder={translate("Detailed biography…")}
                                        rows={3}
                                        // required
                                    />
                                </StyledWrapper>

                                {/* Error message — same style as Education / Personal Interests */}
                                {formErrors.bio_full && (
                                    <span style={{ fontSize: 11, color: T.red500, display: "block" }}>
                                        {formErrors.bio_full}
                                    </span>
                                )}
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="education">
                            <Input
                                label={translate("Education")}
                                value={form.education}
                                onChange={fWithMaxLength("education", 1000)}
                                // required
                                disabled={isSaving}
                                error={formErrors.education || maxLengthErrors.education}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItem} data-field-id="trainings_attended">
                            <Input
                                label={translate("Trainings Attended")}
                                value={form.trainings_attended}
                                onChange={fWithMaxLength("trainings_attended", 255)}
                                disabled={isSaving}
                                error={formErrors.trainings_attended || maxLengthErrors.trainings_attended}
                            />
                        </GridItem>
                        <GridItem width={6} data-field-id="personal_interests">
                            <Input
                                label={translate("Personal Interests")}
                                value={form.personal_interests}
                                onChange={fWithMaxLength("personal_interests", 1000)}
                                // required
                                disabled={isSaving}
                                error={formErrors.personal_interests || maxLengthErrors.personal_interests}
                            />
                        </GridItem>
                    </GridContainer>

                    <Divider label={translate("Roles & Permissions")} />
                    <GridContainer {...styles.togglesGridContainer}>
                        <GridItem {...styles.togglesGridItem} data-field-id="is_sme">
                            <Toggle
                                label={translate("Subject Matter Expert (SME)")}
                                description={translate("Assignable to Brands and Product Types")}
                                checked={!!form.is_sme}
                                onChange={v => {
                                    if (!v) {
                                        // Turning off SME: also turn off DSM and clear all related fields
                                        f("is_sme")(false);
                                        f("is_dsm")(false);
                                        f("brand_ids")([]);
                                        f("division_ids")([]);
                                        f("product_type_ids")([]);
                                        setFormErrors(p => {
                                            const n = { ...p };
                                            delete n.is_sme;
                                            delete n.brand_ids;
                                            delete n.division_ids;
                                            delete n.product_type_ids;
                                            return n;
                                        });
                                    } else {
                                        f("is_sme")(true);
                                        setFormErrors(p => {
                                            const n = { ...p };
                                            delete n.is_sme;
                                            return n;
                                        });
                                    }
                                }}
                            />
                            {formErrors.is_sme && (
                                <span style={{ fontSize: 11, color: T.red500, marginTop: 4, display: "block" }}>
                                    {formErrors.is_sme}
                                </span>
                            )}
                        </GridItem>
                        {!!form.is_sme && (
                            <>
                                <GridItem width={12} style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 8 }}>
                                    <StyledWrapper
                                        style={{ display: "flex", flexDirection: "column", width: "100%" }}
                                    >
                                        <StyledWrapper style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                            <label style={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
                                                {translate("Brands")} <span style={{ color: "#dc2626" }}>*</span>
                                            </label>
                                            <Btn
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setBulkBrandModal(true)}
                                            >
                                                {translate("Add Bulk Brands")}
                                            </Btn>
                                        </StyledWrapper>
                                        <MultiSelect
                                            label={undefined}
                                            value={form.brand_ids || []}
                                            onChange={v => {
                                                f("brand_ids")(v);
                                                if (v.length > 0)
                                                    setFormErrors(p => {
                                                        const n = { ...p };
                                                        delete n.brand_ids;
                                                        return n;
                                                    });
                                            }}
                                            brandSettings={form.brand_settings}
                                            onUpdateBrandSetting={(brandId, key, val) => {
                                                f("brand_settings")({
                                                    ...(form.brand_settings ?? {}),
                                                    [brandId]: {
                                                        ...(form.brand_settings?.[brandId] ?? {}),
                                                        [key]: val,
                                                    },
                                                });
                                            }}
                                            required
                                            options={brands
                                                .filter(b => !b.is_deleted)
                                                .map(b => ({ value: b.id, label: b.name }))}
                                            error={formErrors.brand_ids}
                                            dropdownId="brands" // ← add
                                            openDropdown={openDropdown} // ← add
                                            onOpenDropdown={setOpenDropdown}
                                        />
                                        {/* ── Per-brand checkboxes ── */}
                                    </StyledWrapper>
                                </GridItem>
                                <GridItem width={12} style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 8 }}>
                                    <StyledWrapper style={{ display: "flex", flexDirection: "column", width: "100%" }}>
                                        <StyledWrapper style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                            <label style={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>
                                                {translate("Product Types")}
                                            </label>
                                            <Btn
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => setBulkProductTypeModal(true)}
                                            >
                                                {translate("Add Bulk Product Types")}
                                            </Btn>
                                        </StyledWrapper>
                                        <MultiSelect
                                            label={undefined}
                                            value={form.product_type_ids || []}
                                            brandSettings={form.product_settings}
                                            onUpdateBrandSetting={(productTypeId, key, val) => {
                                                f("product_settings")({
                                                    ...(form.product_settings ?? {}),
                                                    [productTypeId]: {
                                                        ...(form.product_settings?.[productTypeId] ?? {}),
                                                        [key]: val,
                                                    },
                                                });
                                            }}
                                            dropdownId="productTypes"
                                            openDropdown={openDropdown}
                                            onOpenDropdown={setOpenDropdown}
                                            onChange={v => {
                                                f("product_type_ids")(v);
                                                if (v.length > 0)
                                                    setFormErrors(p => {
                                                        const n = { ...p };
                                                        delete n.product_type_ids;
                                                        return n;
                                                    });
                                            }}
                                            options={productTypes
                                                .filter(pt => !pt.is_deleted)
                                                .map(pt => ({ value: pt.id, label: pt.name }))}
                                            error={formErrors.product_type_ids}
                                        />
                                    </StyledWrapper>
                                </GridItem>
                            </>
                        )}
                        <GridItem {...styles.togglesGridItem} data-field-id="division_id">
                            <Toggle
                                label={translate("District Sales Manager (DSM)")}
                                description={translate("Assignable to Divisions (requires SME)")}
                                checked={!!form.is_dsm}
                                disabled={!form.is_sme}
                                onChange={v => {
                                    if (v && !form.is_sme) {
                                        setFormErrors(p => ({
                                            ...p,
                                            is_dsm: "",
                                        }));
                                        return;
                                    }
                                    f("is_dsm")(v);
                                    if (!v) {
                                        f("division_id")("");
                                        setFormErrors(p => {
                                            const n = { ...p };
                                            delete n.division_id;
                                            delete n.is_dsm;
                                            return n;
                                        });
                                    }
                                }}
                            />
                            {formErrors.is_dsm && (
                                <span style={{ fontSize: 11, color: T.red500, marginTop: 4, display: "block" }}>
                                    {formErrors.is_dsm}
                                </span>
                            )}
                        </GridItem>

                        {!!form.is_dsm && (
                            <GridItem width={12} style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 8 }}>
                                <SearchableSelect
                                    label={translate("Division")}
                                    value={form.division_id ?? ""}
                                    onChange={v => {
                                        f("division_id")(v);
                                        if (v) setFormErrors(p => ({ ...p, division_id: "" }));
                                    }}
                                    required
                                    error={formErrors.division_id}
                                    options={divisions
                                        .filter((b: any) => !b.is_deleted)
                                        .map((b: any) => ({ value: b.id, label: b.name }))}
                                    placeholder="Search divisions…"
                                    dropdownId="division" // ← add
                                    openDropdown={openDropdown} // ← add
                                    onOpenDropdown={setOpenDropdown}
                                />
                            </GridItem>
                        )}

                        <GridItem {...styles.togglesGridItem}>
                            <Toggle
                                label={translate("Active Employee")}
                                description={translate("Visible in directories and searches")}
                                checked={!!form.is_active}
                                onChange={f("is_active")}
                            />
                        </GridItem>
                        <GridItem {...styles.togglesGridItem}>
                            <Toggle
                                label={translate("Allow Text Messages")}
                                description={translate("Enables SMS contact preference")}
                                checked={!!form.is_textmessage_allowed}
                                onChange={f("is_textmessage_allowed")}
                            />
                        </GridItem>
                    </GridContainer>
                </Modal>
            )}

            {/* ── Bulk Brands Modal ── */}
            {bulkBrandModal && (
                <Modal
                    title={translate("Add Bulk Brands")}
                    onClose={() => {
                        setBulkBrandModal(false);
                        setBulkBrandText("");
                    }}
                    onSave={handleAddBulkBrands}
                    saveLabel={translate("Add Brands")}
                    maxWidth={500}
                >
                    <GridContainer>
                        <GridItem width={12}>
                            <Field label={translate("Brand Names")}>
                                <textarea
                                    value={bulkBrandText}
                                    onChange={e => setBulkBrandText(e.target.value)}
                                    placeholder={translate("Enter brand names separated by commas")}
                                    style={{
                                        width: "100%",
                                        minHeight: "120px",
                                        padding: "10px 12px",
                                        border: `1.5px solid #d1d5db`,
                                        borderRadius: 7,
                                        fontSize: 13,
                                        fontFamily: "inherit",
                                        outline: "none",
                                        boxSizing: "border-box",
                                        resize: "vertical",
                                    }}
                                />
                            </Field>
                        </GridItem>
                    </GridContainer>
                </Modal>
            )}

            {/* ── Bulk Product Types Modal ── */}
            {bulkProductTypeModal && (
                <Modal
                    title={translate("Add Bulk Product Types")}
                    onClose={() => {
                        setBulkProductTypeModal(false);
                        setBulkProductTypeText("");
                    }}
                    onSave={handleAddBulkProductTypes}
                    saveLabel={translate("Add Product Types")}
                    maxWidth={500}
                >
                    <GridContainer>
                        <GridItem width={12}>
                            <Field label={translate("Product Type Names")}>
                                <textarea
                                    value={bulkProductTypeText}
                                    onChange={e => setBulkProductTypeText(e.target.value)}
                                    placeholder={translate("Enter product types separated by commas")}
                                    style={{
                                        width: "100%",
                                        minHeight: "120px",
                                        padding: "10px 12px",
                                        border: `1.5px solid #d1d5db`,
                                        borderRadius: 7,
                                        fontSize: 13,
                                        fontFamily: "inherit",
                                        outline: "none",
                                        boxSizing: "border-box",
                                        resize: "vertical",
                                    }}
                                />
                            </Field>
                        </GridItem>
                    </GridContainer>
                </Modal>
            )}

            {del && (
                <ConfirmModal
                    message={translate(`Delete "${del.first_name} ${del.last_name}"? This action cannot be undone.`)}
                    onConfirm={() => {
                        deleteEmployeeData({
                            employeeId: del.id,
                            onSuccess: () => {
                                setEmployees(p => p.filter(e => e.id !== del.id));
                                setDel(null);
                                setRefreshCount(c => c + 1);
                                setSearch("")
                            },
                            onError: (error: string) => {
                                setSaveError(error);
                                setDel(null);
                            },
                        });
                    }}
                    onCancel={() => setDel(null)}
                />
            )}
            {employees.length > 0 && (
                <Pagination
                    {...styles.pagination}
                    currentPage={employeesPagination?.currentPage || 1}
                    resultsPerPage={employeesPagination?.pageSize}
                    resultsCount={employeesPagination?.totalItemCount || 0}
                    resultsPerPageOptions={employeesPagination?.pageSizeOptions}
                    onChangePage={changePageHandler}
                    onChangeResultsPerPage={changeResultsPerPageHandler}
                    prevPage={employeesPagination?.prevPageUri}
                    nextPage={employeesPagination?.nextPageUri}
                />
            )}
        </StyledWrapper>
    );
};

// ─── BRANCHES PAGE ────────────────────────────────────────────────────────────

const BranchesPage: FC<BranchesPageProps> = ({
    showSearch,
    addBranchData,
    getBranchCollectionData,
    updateBranchData,
    deleteBranchData,
    onBranchesLoaded,
    getBranchByIdHandler,
    signInUrl,
}) => {
    // ── State ──────────────────────────────────────────────────────────
    const [branches, setBranches] = useState<any[]>([]);
    const [branchesPagination, setBranchesPagination] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    // const [search, setSearch] = useState("");
    const [modal, setModal] = useState<string | null>(null);
    const [form, setForm] = useState<any>({});
    const [del, setDel] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [search, setSearch] = useState("");
    const [refreshCount, setRefreshCount] = useState(0);

    const blank = { name: "", address_line1: "", address_line2: "", city: "", state: "", zip_code: "" };

    // ── Helper to map API model → local shape ──────────────────────────
    const mapToLocal = (b: BranchModel) => ({
        id: b.id,
        name: b.name,
        address_line1: b.addressLine1,
        address_line2: b.addressLine2 || "",
        city: b.city,
        state: b.state,
        zip_code: b.zipCode,
        is_deleted: false,
    });

    // ── Load from API on mount ─────────────────────────────────────────
    React.useEffect(() => {
        setIsLoading(true);
        setLoadError(null);
        getBranchCollectionData({
            page: 1,

            onSuccess: (result: BranchCollectionModel) => {
                setBranches((result.branchModels ?? []).map(mapToLocal));
                setIsLoading(false);
                setBranchesPagination(result.pagination);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    }, [refreshCount]);
    const changePageHandler = (newPageIndex: number) => {
        getBranchCollectionData({
            page: newPageIndex,
            pageSize: branchesPagination.pageSize,
            query: search,
            onSuccess: (result: BranchCollectionModel) => {
                setBranches((result.branchModels ?? []).map(mapToLocal));
                setBranchesPagination(result.pagination);
            },
            onError: (error: string) => {
                setLoadError(error);
            },
        });
    };

    const changeResultsPerPageHandler = (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        getBranchCollectionData({
            page: 1,
            pageSize: newPageSize,
            onSuccess: (result: BranchCollectionModel) => {
                setBranches((result.branchModels ?? []).map(mapToLocal));
                setBranchesPagination(result.pagination);
            },
            onError: (error: string) => {
                setLoadError(error);
            },
        });
    };
    const getBranch = async (obj: any) => {
        const data = { ...obj };

        const id = data.id;

        getBranchByIdHandler({
            id,
            onSuccess: (result: any) => {
                setBranches(p =>
                    p.map(x =>
                        x.id === result.id ? mapToLocal({ ...result, branchId: result.branchId ?? form.branch_id }) : x,
                    ),
                );
                setForm({ ...obj });
                setSaveError(null);
                setModal("edit");
            },
        });
    };
    // const filtered = branches.filter(
    //     b => !b.is_deleted && `${b.name} ${b.city} ${b.state}`.toLowerCase().includes(search.toLowerCase()),
    // );
    // filtered removed - use server-side search results directly
    const activeBranches = branches.filter(b => !b.is_deleted);

    const f = (k: string) => (v: any) => setForm((p: any) => ({ ...p, [k]: v }));

    // ── Save handler ───────────────────────────────────────────────────
    const save = () => {
        const errors: Record<string, string> = {};
        if (!form.name?.trim()) errors.name = translate("Branch Name is required.");
        if (!form.address_line1?.trim()) errors.address_line1 = translate("Address Line 1 is required.");
        if (!form.city?.trim()) errors.city = translate("City is required.");
        if (!form.state?.trim()) errors.state = translate("State is required.");
        if (!form.zip_code?.trim()) errors.zip_code = translate("ZIP Code is required.");

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        // ── Duplicate name check against local list ──
        const isDuplicate = branches.some(
            b => !b.is_deleted && b.name.trim().toLowerCase() === form.name.trim().toLowerCase() && b.id !== form.id,
        );
        if (isDuplicate) {
            setFieldErrors(p => ({ ...p, name: translate("A branch with this name already exists.") }));
            return;
        }

        setFieldErrors({});
        setSaveError(null);
        setIsSaving(true);

        // ── Shared API error handler that maps "already exists" to field error ──
        const handleApiError = (error: string) => {
            if (isAuthError(error)) {
                setSaveError("__AUTH__"); // sentinel value
                setIsSaving(false);
                return;
            }
            const lower = (error ?? "").toLowerCase();
            if (lower.includes("already exists") || lower.includes("duplicate") || lower.includes("name")) {
                setFieldErrors(p => ({ ...p, name: translate("A branch with this name already exists.") }));
            } else {
                setSaveError(error || translate("Something went wrong. Please try again."));
            }
            setIsSaving(false);
        };

        if (modal === "create") {
            addBranchData({
                model: {
                    name: form.name.trim(),
                    addressLine1: form.address_line1.trim(),
                    addressLine2: form.address_line2?.trim() || "",
                    city: form.city.trim(),
                    state: form.state.trim(),
                    zipCode: form.zip_code.trim(),
                },
                onSuccess: (result: BranchModel) => {
                    setBranches(p => [...p, mapToLocal(result)]);
                    setIsSaving(false);
                    setModal(null);
                },
                onError: handleApiError,
            });
        } else {
            updateBranchData({
                model: {
                    id: form.id,
                    name: form.name.trim(),
                    addressLine1: form.address_line1.trim(),
                    addressLine2: form.address_line2?.trim() || "",
                    city: form.city.trim(),
                    state: form.state.trim(),
                    zipCode: form.zip_code.trim(),
                },
                onSuccess: (result: BranchModel) => {
                    setBranches(p => p.map(b => (b.id === result.id ? mapToLocal(result) : b)));
                    setIsSaving(false);
                    setModal(null);
                },
                onError: handleApiError,
            });
        }
    };

    // ── Loading state ──────────────────────────────────────────────────
    if (isLoading) {
        return (
            <StyledWrapper
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "80px 0",
                    color: T.gray400,
                    fontSize: 13,
                    gap: 10,
                }}
            >
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.gray300}
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ animation: "spin 1s linear infinite" }}
                >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                {translate("Loading branches…")}
            </StyledWrapper>
        );
    }

    // ── Load error state ───────────────────────────────────────────────
    if (loadError) {
        return (
            <StyledWrapper>
                {isAuthError(loadError) ? (
                    <SessionExpiredBanner signInUrl={signInUrl} />
                ) : (
                    <StyledWrapper
                        style={{
                            background: T.red50,
                            border: `1px solid #fecaca`,
                            borderRadius: 8,
                            padding: "16px 20px",
                            fontSize: 13,
                            color: T.red600,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                        }}
                    >
                        <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Ico d={IC.alert} size={15} color={T.red500} />
                            {translate("Failed to load branches: ")} {loadError}
                        </StyledWrapper>
                        <Btn
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                /* retry logic */
                            }}
                        >
                            {translate("Retry")}
                        </Btn>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }
    const handleSearch = () => {
        setIsLoading(true);
        setLoadError(null);

        getBranchCollectionData({
            page: 1,
            pageSize: branchesPagination?.pageSize || 50,
            query: search.trim(),
            onSuccess: (result: BranchCollectionModel) => {
                setBranches((result.branchModels ?? []).map(mapToLocal));
                setBranchesPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };
    // ── Normal render ──────────────────────────────────────────────────
    return (
        <StyledWrapper data-test-selector="adminBranchesPage">
            <PageHeader
                title="Branches"
                subtitle="Office locations and addresses"
                count={branchesPagination.totalItemCount}
                search={search}
                onSearch={v => {
                    setSearch(v);
                    if (v === "") {
                        setIsLoading(true);
                        getBranchCollectionData({
                            page: 1,
                            pageSize: branchesPagination?.pageSize || 50,
                            query: "",
                            onSuccess: result => {
                                setBranches((result.branchModels ?? []).map(mapToLocal));
                                setBranchesPagination(result.pagination);
                                setIsLoading(false);
                            },
                            onError: error => {
                                setLoadError(error);
                                setIsLoading(false);
                            },
                        });
                    }
                }}
                onSearchClick={handleSearch}
                showSearch={showSearch}
                onClearSearch={() => {
                    setSearch("");
                    getBranchCollectionData({
                        page: 1,
                        pageSize: branchesPagination?.pageSize || 50,
                        query: "",
                        onSuccess: result => {
                            setBranches((result.branchModels ?? []).map(mapToLocal));
                            setBranchesPagination(result.pagination);
                        },
                        onError: error => setLoadError(error),
                    });
                }}
                addLabel="New Branch"
                onAdd={() => {
                    setForm({ ...blank });
                    setSaveError(null);
                    setModal("create");
                }}
            />
            <StyledWrapper // ← ADD THIS
                style={{
                    width: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    borderRadius: 8,
                }}
            >
                {" "}
                <StyledWrapper style={{ minWidth: 800 }}>
                    <DataTable
                        columns={[
                            {
                                key: "name",
                                label: translate("Branch Name"),
                                render: (v: string) => <span style={{ fontWeight: 700, color: T.gray900 }}>{v}</span>,
                            },
                            {
                                key: "address_line1",
                                label: translate("Address"),
                                render: (v: string, r: any) => `${v}${r.address_line2 ? `, ${r.address_line2}` : ""}`,
                            },
                            { key: "city", label: translate("City") },
                            { key: "state", label: translate("State") },
                            { key: "zip_code", label: translate("ZIP") },
                        ]}
                        rows={activeBranches}
                        onEdit={async b => {
                            try {
                                await getBranch(b);
                                setModal("edit");
                            } catch (error: any) {
                                setSaveError(`Failed to load branch: ${error.message || error}`);
                            }
                        }}
                        onDelete={b => setDel(b)}
                    />{" "}
                </StyledWrapper>
            </StyledWrapper>

            {modal && (
                <Modal
                    title={modal === "create" ? translate("New Branch") : translate("Edit Branch")}
                    subtitle={modal === "edit" ? form.name : ""}
                    onClose={() => {
                        setModal(null);
                        setSaveError(null);
                        setFieldErrors({});
                    }}
                    onSave={save}
                    saveLabel={isSaving ? "Saving…" : modal === "create" ? "Create Branch" : "Save Changes"}
                    maxWidth={580}
                >
                    {saveError && (
                        <StyledWrapper
                            style={{
                                background: saveError === "__AUTH__" ? "#fffbeb" : T.red50,
                                border: `1px solid ${saveError === "__AUTH__" ? "#fcd34d" : "#fecaca"}`,
                                borderRadius: 7,
                                padding: "10px 14px",
                                marginBottom: 16,
                                fontSize: 13,
                                color: saveError === "__AUTH__" ? "#92400e" : T.red600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                            }}
                        >
                            <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Ico d={IC.alert} size={14} color={saveError === "__AUTH__" ? "#d97706" : T.red500} />
                                {saveError === "__AUTH__"
                                    ? translate("Your session has expired. Please Login again to continue.")
                                    : saveError}
                            </StyledWrapper>
                            {saveError === "__AUTH__" && (
                                <Btn
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        window.location.href = signInUrl || "/";
                                    }}
                                >
                                    {translate("Login")}
                                </Btn>
                            )}
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.formGridContainer}>
                        <GridItem width={12}>
                            <Input
                                label={translate("Branch Name")}
                                value={form.name}
                                onChange={v => {
                                    f("name")(v);
                                    setFieldErrors(p => ({ ...p, name: "" }));
                                }}
                                required
                                disabled={isSaving}
                                error={fieldErrors.name} // ← shows red message under the field
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Input
                                label={translate("Address Line 1")}
                                value={form.address_line1}
                                onChange={v => {
                                    f("address_line1")(v);
                                    setFieldErrors(p => ({ ...p, address_line1: "" }));
                                }}
                                required
                                disabled={isSaving}
                                error={fieldErrors.address_line1}
                            />
                        </GridItem>
                        <GridItem width={12}>
                            <Input
                                label={translate("Address Line 2")}
                                value={form.address_line2}
                                onChange={f("address_line2")}
                                placeholder={translate("Suite, Floor, etc. (optional)")}
                                disabled={isSaving}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItem}>
                            <Input
                                label={translate("City")}
                                value={form.city}
                                onChange={v => {
                                    f("city")(v);
                                    setFieldErrors(p => ({ ...p, city: "" }));
                                }}
                                required
                                disabled={isSaving}
                                error={fieldErrors.city}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItemHalf}>
                            <Input
                                label={translate("State")}
                                value={form.state}
                                onChange={v => {
                                    f("state")(v);
                                    setFieldErrors(p => ({ ...p, state: "" }));
                                }}
                                required
                                disabled={isSaving}
                                error={fieldErrors.state}
                            />
                        </GridItem>
                        <GridItem {...styles.formGridItemThird}>
                            <Input
                                label={translate("ZIP")}
                                value={form.zip_code}
                                onChange={v => {
                                    f("zip_code")(v);
                                    setFieldErrors(p => ({ ...p, zip_code: "" }));
                                }}
                                required
                                disabled={isSaving}
                                error={fieldErrors.zip_code}
                            />
                        </GridItem>
                    </GridContainer>
                </Modal>
            )}

            {del && (
                <ConfirmModal
                    message={translate(`Delete branch "${del.name}"?`)}
                    onConfirm={() => {
                        deleteBranchData({
                            branchId: del.id,
                            onSuccess: () => {
                                // ← no parameter now
                                setBranches(p => p.filter(b => b.id !== del.id));
                                setDel(null);
                                setRefreshCount(c => c + 1);
                                setSearch("")
                            },
                            onError: (error: string) => {
                                setSaveError(error);
                                setDel(null);
                            },
                        });
                    }}
                    onCancel={() => setDel(null)}
                />
            )}
            {branches.length > 0 && (
                <Pagination
                    {...styles.pagination}
                    currentPage={branchesPagination?.currentPage || 1}
                    resultsPerPage={branchesPagination?.pageSize}
                    resultsCount={branchesPagination?.totalItemCount || 0}
                    resultsPerPageOptions={branchesPagination?.pageSizeOptions}
                    onChangePage={changePageHandler}
                    onChangeResultsPerPage={changeResultsPerPageHandler}
                    prevPage={branchesPagination?.prevPageUri}
                    nextPage={branchesPagination?.nextPageUri}
                />
            )}
        </StyledWrapper>
    );
};
// ─── DIVISIONS PAGE ───────────────────────────────────────────────────────────

const DivisionsPage: FC<DivisionsPageProps> = ({
    addDivisionData,
    getDivisionCollectionData,
    updateDivisionData,
    deleteDivisionData,
    onDivisionsLoaded,
    getBrandsCollectionData,
    getDivisionByIdHandler,
    signInUrl,
}) => {
    const [divisions, setDivisions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [divisionsPagination, setDivisionsPagination] = useState<any>();
    const [loadError, setLoadError] = useState<string | null>(null);
    const [modal, setModal] = useState<string | null>(null);
    const [form, setForm] = useState<any>({});
    const [del, setDel] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({}); // ← replaces fieldError
    const [brands, setBrands] = useState<any[]>([]);
    const [refreshCount, setRefreshCount] = useState(0);

    const mapToLocal = (d: DivisionModel) => ({
        id: d.id,
        name: d.name,
        productTypeCount: d.productTypeCount ?? 0,
        is_deleted: false,
        brand_ids: d.brandIds ?? [],
    });
    React.useEffect(() => {
        getBrandsCollectionData({
            pageSize: 3000,
            onSuccess: (result: BrandsCollectionModel) => {
                const brandData = (result.brandsList ?? []).map(d => ({
                    id: d.id,
                    name: d.name,
                }));
                setBrands(brandData);
            },
            onError: error => { },
        });
    }, []);
    React.useEffect(() => {
        setIsLoading(true);
        setLoadError(null);
        getDivisionCollectionData({
            page: 1,

            onSuccess: (result: DivisionCollectionModel) => {
                const mapped = (result.divisionModels ?? []).map(mapToLocal);
                setDivisions(mapped);
                setDivisionsPagination(result.pagination);
                setIsLoading(false);
                // ── Bubble up to parent so ProductTypes can use live divisions ──
                if (onDivisionsLoaded) {
                    onDivisionsLoaded(mapped);
                }
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    }, [refreshCount]);
    const changePageHandler = (newPageIndex: number) => {
        getDivisionCollectionData({
            page: newPageIndex,
            pageSize: divisionsPagination.pageSize,
            onSuccess: (result: DivisionCollectionModel) => {
                const mapped = (result.divisionModels ?? []).map(mapToLocal);
                setDivisions(mapped);
                setDivisionsPagination(result.pagination);
                setIsLoading(false);
                // ── Bubble up to parent so ProductTypes can use live divisions ──
                if (onDivisionsLoaded) {
                    onDivisionsLoaded(mapped);
                }
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };
    const changeResultsPerPageHandler = (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        getDivisionCollectionData({
            page: 1,
            pageSize: newPageSize,
            onSuccess: (result: DivisionCollectionModel) => {
                const mapped = (result.divisionModels ?? []).map(mapToLocal);
                setDivisions(mapped);
                setDivisionsPagination(result.pagination);
                setIsLoading(false);
                // ── Bubble up to parent so ProductTypes can use live divisions ──
                if (onDivisionsLoaded) {
                    onDivisionsLoaded(mapped);
                }
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };

    const active = divisions.filter(d => !d.is_deleted);

    const save = () => {
        // ── Collect all errors at once ──
        const errors: Record<string, string> = {};
        if (!form.name?.trim()) errors.name = translate("Division Name is required.");

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors); // ← replaces setFieldError
            return;
        }

        // ── Duplicate name check ──
        const isDuplicate = divisions.some(
            d => !d.is_deleted && d.name.trim().toLowerCase() === form.name.trim().toLowerCase() && d.id !== form.id,
        );
        if (isDuplicate) {
            setFieldErrors(p => ({ ...p, name: translate("A division with this name already exists.") }));
            return;
        }

        setFieldErrors({}); // ← replaces setFieldError("")
        setSaveError(null);
        setIsSaving(true);

        if (modal === "create") {
            addDivisionData({
                model: {
                    name: form.name.trim(),
                    brandIds: form.brand_ids ?? [],
                },
                onSuccess: (result: DivisionModel) => {
                    const newEntry = { ...mapToLocal(result), productTypeCount: 0 };
                    const updated = [...divisions, newEntry];
                    setDivisions(updated);
                    if (onDivisionsLoaded) onDivisionsLoaded(updated.filter(d => !d.is_deleted));
                    setIsSaving(false);
                    setModal(null);
                },
                onError: (error: string) => {
                    if (isAuthError(error)) {
                        setSaveError("__AUTH__");
                        setIsSaving(false);
                        return;
                    }
                    setSaveError(error || translate("Something went wrong. Please try again."));
                    setIsSaving(false);
                },
            });
        } else {
            updateDivisionData({
                model: {
                    id: form.id,
                    name: form.name.trim(),
                    brandIds: form.brand_ids ?? [],
                },
                onSuccess: (result: DivisionModel) => {
                    const updated = divisions.map(d =>
                        d.id === result.id
                            ? { ...mapToLocal(result), productTypeCount: result.productTypeCount ?? d.productTypeCount }
                            : d,
                    );
                    setDivisions(updated);
                    if (onDivisionsLoaded) onDivisionsLoaded(updated.filter(d => !d.is_deleted));
                    setIsSaving(false);
                    setModal(null);
                },
                onError: (error: string) => {
                    if (isAuthError(error)) {
                        setSaveError("__AUTH__");
                        setIsSaving(false);
                        return;
                    }
                    setSaveError(error || translate("Something went wrong. Please try again."));
                    setIsSaving(false);
                },
            });
        }
    };

    if (isLoading) {
        return (
            <StyledWrapper
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "80px 0",
                    color: T.gray400,
                    fontSize: 13,
                    gap: 10,
                }}
            >
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.gray300}
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ animation: "spin 1s linear infinite" }}
                >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                {translate("Loading divisions…")}
            </StyledWrapper>
        );
    }

    if (loadError) {
        return isAuthError(loadError) ? (
            <SessionExpiredBanner signInUrl={signInUrl} />
        ) : (
            <StyledWrapper
                style={{
                    background: T.red50,
                    border: `1px solid #fecaca`,
                    borderRadius: 8,
                    padding: "16px 20px",
                    fontSize: 13,
                    color: T.red600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                }}
            >
                <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Ico d={IC.alert} size={15} color={T.red500} />
                    {translate("Failed to load: ")} {loadError}
                </StyledWrapper>
                <Btn variant="secondary" size="sm" onClick={() => window.location.reload()}>
                    {translate("Retry")}
                </Btn>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper data-test-selector="adminDivisionsPage">
            <PageHeader
                title="Divisions"
                subtitle="Organizational divisions and business units"
                count={divisionsPagination.totalItemCount}
                addLabel="New Division"
                onAdd={() => {
                    setForm({ name: "", brand_ids: [] });
                    setSaveError(null);
                    setFieldErrors({});
                    setModal("create");
                }}
            />

            {saveError && !modal && (
                <StyledWrapper
                    style={{
                        background: saveError === "__AUTH__" ? "#fffbeb" : T.red50, // ← fix
                        border: `1px solid ${saveError === "__AUTH__" ? "#fcd34d" : "#fecaca"}`, // ← fix
                        borderRadius: 7,
                        padding: "10px 14px",
                        marginBottom: 16,
                        fontSize: 13,
                        color: saveError === "__AUTH__" ? "#92400e" : T.red600, // ← fix
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between", // ← fix
                        gap: 8,
                    }}
                >
                    <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Ico d={IC.alert} size={14} color={saveError === "__AUTH__" ? "#d97706" : T.red500} />
                        {saveError === "__AUTH__"
                            ? translate("Your session has expired. Please Login again to continue.")
                            : saveError}
                    </StyledWrapper>
                    {saveError === "__AUTH__" && (
                        <Btn
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                window.location.href = signInUrl || "/";
                            }}
                        >
                            {translate("Login")}
                        </Btn>
                    )}
                </StyledWrapper>
            )}

            <StyledWrapper // ← ADD THIS
                style={{
                    width: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    borderRadius: 8,
                }}
            >
                <StyledWrapper style={{ minWidth: 600 }}>
                    <DataTable
                        columns={[
                            {
                                key: "name",
                                label: translate("Division Name"),
                                render: (v: string) => <span style={{ fontWeight: 700, color: T.gray900 }}>{v}</span>,
                            },
                        ]}
                        rows={active}
                        onEdit={async d => {
                            try {
                                await new Promise(resolve => {
                                    getDivisionByIdHandler({
                                        id: d.id,
                                        onSuccess: result => {
                                            setForm(mapToLocal(result));
                                            resolve(result);
                                        },
                                    });
                                });
                                setSaveError(null);
                                setModal("edit");
                            } catch (error: any) {
                                setSaveError(`Failed to load division: ${error.message}`);
                            }
                        }}
                        onDelete={d => setDel(d)}
                    />{" "}
                </StyledWrapper>
            </StyledWrapper>

            {modal && (
                <Modal
                    title={modal === "create" ? translate("New Division") : translate("Edit Division")}
                    subtitle={modal === "edit" ? form.name : ""}
                    onClose={() => {
                        setModal(null);
                        setSaveError(null);
                        setFieldErrors({});
                    }}
                    onSave={save}
                    saveLabel={isSaving ? "Saving…" : modal === "create" ? "Create Division" : "Save Changes"}
                    // disabled={!form.name?.trim()}
                    maxWidth={440}
                >
                    {saveError && (
                        <StyledWrapper
                            style={{
                                background: saveError === "__AUTH__" ? "#fffbeb" : T.red50,
                                border: `1px solid ${saveError === "__AUTH__" ? "#fcd34d" : "#fecaca"}`,
                                borderRadius: 7,
                                padding: "10px 14px",
                                marginBottom: 16,
                                fontSize: 13,
                                color: saveError === "__AUTH__" ? "#92400e" : T.red600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                            }}
                        >
                            <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Ico d={IC.alert} size={14} color={saveError === "__AUTH__" ? "#d97706" : T.red500} />
                                {saveError === "__AUTH__"
                                    ? translate("Your session has expired. Please Login again to continue.")
                                    : saveError}
                            </StyledWrapper>
                            {saveError === "__AUTH__" && (
                                <Btn
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        window.location.href = signInUrl || "/";
                                    }}
                                >
                                    {translate("Login")}
                                </Btn>
                            )}
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.formGridContainer} style={{ minHeight: 200 }}>
                        <GridItem width={12} style={{ width: "100%", marginBottom: 10 }}>
                            <Input
                                label={translate("Division Name")}
                                value={form.name}
                                onChange={v => {
                                    setForm((p: any) => ({ ...p, name: v }));
                                    if (v.trim()) setFieldErrors(p => ({ ...p, name: "" })); // ← updated
                                }}
                                required
                                disabled={isSaving}
                                error={fieldErrors.name}
                            />
                        </GridItem>
                        <GridItem width={12} style={{ width: "100%", marginBottom: 10 }}>
                            <MultiSelect
                                label="Brands"
                                value={form.brand_ids || []}
                                onChange={v => {
                                    setForm((p: any) => ({ ...p, brand_ids: v }));
                                    if (v.length > 0) setFieldErrors(p => ({ ...p, brand_ids: "" })); // ← add
                                }}
                                options={brands.filter(b => !b.is_deleted).map(b => ({ value: b.id, label: b.name }))}
                            />
                        </GridItem>
                    </GridContainer>
                </Modal>
            )}

            {del && (
                <ConfirmModal
                    message={translate(`Delete division "${del.name}"?`)}
                    onConfirm={() => {
                        deleteDivisionData({
                            divisionId: del.id,
                            onSuccess: () => {
                                const updated = divisions.filter(d => d.id !== del.id);
                                setDivisions(updated);
                                if (onDivisionsLoaded) onDivisionsLoaded(updated.filter(d => !d.is_deleted));
                                setDel(null);
                                setRefreshCount(c => c + 1);
                            },
                            onError: (error: string) => {
                                setSaveError(error);
                                setDel(null);
                            },
                        });
                    }}
                    onCancel={() => setDel(null)}
                />
            )}
            {divisions.length > 0 && (
                <Pagination
                    {...styles.pagination}
                    currentPage={divisionsPagination?.currentPage || 1}
                    resultsPerPage={divisionsPagination?.pageSize || 50}
                    resultsCount={divisionsPagination?.totalItemCount || 0}
                    resultsPerPageOptions={divisionsPagination?.pageSizeOptions || [10, 25, 50]}
                    onChangePage={changePageHandler}
                    onChangeResultsPerPage={changeResultsPerPageHandler}
                    prevPage={divisionsPagination?.prevPageUri}
                    nextPage={divisionsPagination?.nextPageUri}
                />
            )}
        </StyledWrapper>
    );
};

// ─── PRODUCT TYPES PAGE ───────────────────────────────────────────────────────

const ProductTypesPage: FC<ProductTypesPageProps> = ({
    addProductTypeData,
    getProductTypeById,
    getProductTypeCollectionData,
    updateProductTypeData,
    deleteProductTypeData,
    divisions: divisionsProp,
    getDivisionCollectionData,
    signInUrl,
    showSearch,
}) => {
    const [products, setProducts] = useState<any[]>([]);
    const [productsPagination, setProductsPagination] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [modal, setModal] = useState<string | null>(null);
    const [form, setForm] = useState<any>({});
    const [del, setDel] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [divisions, setDivisions] = useState<any[]>(divisionsProp ?? []);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");

    const handleSearch = () => {
        setIsLoading(true);
        setLoadError(null);
        getProductTypeCollectionData({
            page: 1,
            pageSize: productsPagination?.pageSize || 50,
            query: search.trim(),
            onSuccess: (result: ProductTypeCollectionModel) => {
                setProducts((result.productTypeModels ?? []).map(mapToLocal));
                setProductsPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };
    const [refreshCount, setRefreshCount] = useState(0);

    // photo states and handlers start here
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string>("");
    const [photoError, setPhotoError] = useState<string>("");
    const photoUploadRef = React.useRef({ value: "" } as HTMLInputElement);
    const origin = "userfiles";
    const validatePhoto = (file: File | null) => {
        if (!file) {
            setPhotoError("");
            return true;
        }
        const allowedExtensions = ["jpg", "jpeg", "png", "webp"];
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        if (!allowedExtensions.includes(ext)) {
            setPhotoError("Photo must be .jpg, .jpeg, .png, or .webp format");
            return false;
        }
        const maxSizeMB = 5;
        if (file.size > maxSizeMB * 1024 * 1024) {
            setPhotoError(`Photo must be smaller than ${maxSizeMB}MB`);
            return false;
        }
        setPhotoError("");
        return true;
    };

    const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        if (file) {
            const isValid = validatePhoto(file);
            if (isValid) {
                setPhotoFile(file);
                setFieldErrors(p => {
                    const n = { ...p };
                    delete n.image;
                    return n;
                });

                const reader = new FileReader();
                reader.onloadend = () => {
                    setPhotoPreviewUrl(reader.result as string);
                };
                reader.readAsDataURL(file);
            } else {
                setPhotoFile(null);
                setPhotoPreviewUrl("");
            }
        } else {
            setPhotoFile(null);
            setPhotoPreviewUrl("");
            setPhotoError("");
        }
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreviewUrl("");
        setPhotoError("");
        photoUploadRef.current.value = "";
    };

    const getPhotoBase64 = (): Promise<string> => {
        return new Promise(resolve => {
            if (!photoFile) {
                resolve("");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                const base64 = result.split(",")[1] ?? "";
                resolve(base64);
            };
            reader.readAsDataURL(photoFile);
        });
    };

    // photo states and handlers end here

    const handleClose = () => {
        setErrors({});
        setModal(null);
    };

    const mapToLocal = (p: ProductTypeModel) => ({
        id: p.id,
        name: p.name,
        division_id: p.divisionId, // ← map divisionName → local division_id for display lookup
        ImageBase64: p.photoFile ?? "", // ← photo mapping
        is_deleted: false,
    });

    // Load on mount
    React.useEffect(() => {
        setIsLoading(true);
        getProductTypeCollectionData({
            page: 1,
            onSuccess: (result: ProductTypeCollectionModel) => {
                setProducts((result.productTypeModels ?? []).map(mapToLocal));
                setIsLoading(false);
                setProductsPagination(result.pagination);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    }, [refreshCount]);
    const changePageHandler = (newPageIndex: number) => {
        getProductTypeCollectionData({
            page: newPageIndex,
            pageSize: productsPagination.pageSize,
            query: search,
            onSuccess: (result: ProductTypeCollectionModel) => {
                setProducts((result.productTypeModels ?? []).map(mapToLocal));
                setProductsPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };
    const changeResultsPerPageHandler = (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        getProductTypeCollectionData({
            page: 1,
            pageSize: newPageSize,
            onSuccess: (result: ProductTypeCollectionModel) => {
                setProducts((result.productTypeModels ?? []).map(mapToLocal));
                setProductsPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };
    // Self-load divisions if parent hasn't provided them yet
    React.useEffect(() => {
        if (divisionsProp && divisionsProp.length > 0) {
            setDivisions(divisionsProp); // parent already has them, use those
        } else {
            // Direct navigation — fetch divisions ourselves ONCE
            getDivisionCollectionData({
                onSuccess: (result: DivisionCollectionModel) => {
                    setDivisions(
                        (result.divisionModels ?? []).map(d => ({
                            id: d.id,
                            name: d.name,
                            imageUrl: d.uri,
                            is_deleted: false,
                        })),
                    );
                },
                onError: () => { },
            });
        }
    }, []);

    const active = products.filter(p => !p.is_deleted);
    const f = (k: string) => (v: any) => setForm((p: any) => ({ ...p, [k]: v }));

    const extractErrorMessage = (error: string): string => {
        const match = error?.match(/errorMessage:\s*([^.]+\.)/);
        if (match) return match[1].trim();
        const jsonMatch = error?.match(/"message"\s*:\s*"([^"]+)"/);
        if (jsonMatch) return jsonMatch[1].trim();
        return error || translate("Something went wrong. Please try again.");
    };

    const save = async () => {
        const errors: Record<string, string> = {};
        if (!form.name?.trim()) errors.name = translate("Product Type Name is required.");
        // if (!form.brand_ids || form.brand_ids.length === 0) errors.brand_ids = translate("Please select at least one brand.");
        if (!form.division_id?.trim()) errors.division_id = translate("Division is required.");

        // if (!photoFile && !photoPreviewUrl) {
        //     errors.image = translate("Product Type Image is required.");
        // }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        // ── Look up division name from selected division id ──
        const selectedDivision = divisions.find(d => d.id === form.division_id);
        if (!selectedDivision) {
            setFieldErrors({ division_id: translate("Selected division is invalid.") });
            return;
        }

        // ── Duplicate name check (same name + same division) ──
        const isDuplicate = products.some(
            p =>
                !p.is_deleted &&
                p.name.trim().toLowerCase() === form.name.trim().toLowerCase() &&
                p.division_id === form.division_id &&
                p.id !== form.id,
        );
        if (isDuplicate) {
            setFieldErrors(p => ({
                ...p,
                name: translate("A product type with this name already exists in the selected division."),
            }));
            return;
        }

        setFieldErrors({});
        setSaveError(null);
        setIsSaving(true);
        const photoBase64 = await getPhotoBase64();

        if (modal === "create") {
            addProductTypeData({
                model: {
                    name: form.name.trim(),
                    divisionId: selectedDivision.id,
                    ImageBase64: photoBase64 // ← add
                        ? `data:image/${photoFile?.name.split(".").pop()?.toLowerCase()};base64,${photoBase64}`
                        : "",
                },
                onSuccess: (result: ProductTypeModel) => {
                    setProducts(p => [
                        ...p,
                        mapToLocal({ ...result, divisionId: result.divisionId ?? form.division_id }),
                    ]);
                    setIsSaving(false);
                    setModal(null);
                },
                onError: (error: string) => {
                    if (isAuthError(error)) {
                        setSaveError("__AUTH__");
                        setIsSaving(false);
                        return;
                    }
                    const msg = extractErrorMessage(error);
                    if (msg.toLowerCase().includes("name") || msg.toLowerCase().includes("exists")) {
                        setFieldErrors({ name: msg });
                    } else {
                        setSaveError(msg);
                    }
                    setIsSaving(false);
                },
            });
        } else {
            updateProductTypeData({
                model: {
                    id: form.id,
                    name: form.name.trim(),
                    divisionId: selectedDivision.id,
                    ImageBase64: photoBase64 // ← add
                        ? `data:image/${photoFile?.name.split(".").pop()?.toLowerCase()};base64,${photoBase64}`
                        : "",
                },
                onSuccess: (result: ProductTypeModel) => {
                    setProducts(p =>
                        p.map(x =>
                            x.id === result.id
                                ? mapToLocal({ ...result, divisionId: result.divisionId ?? form.division_id })
                                : x,
                        ),
                    );
                    setIsSaving(false);
                    setModal(null);
                },
                onError: (error: string) => {
                    if (isAuthError(error)) {
                        setSaveError("__AUTH__");
                        setIsSaving(false);
                        return;
                    }
                    const msg = extractErrorMessage(error);
                    if (msg.toLowerCase().includes("name") || msg.toLowerCase().includes("exists")) {
                        setFieldErrors({ name: msg });
                    } else {
                        setSaveError(msg);
                    }
                    setIsSaving(false);
                },
            });
        }
    };

    // ── Fetch single product type by ID and update local state ─────────
    const getProductType = async (obj: any) => {
        const data = { ...obj };

        const id = data.id;

        getProductTypeById({
            id,
            onSuccess: (result: any) => {
                setProducts(p =>
                    p.map(x =>
                        x.id === result.id
                            ? mapToLocal({ ...result, divisionId: result.divisionId ?? form.division_id })
                            : x,
                    ),
                );
                setForm({ ...obj });
                setSaveError(null);
                setPhotoFile(null); // Set photo states on edit
                setPhotoPreviewUrl(result.imageUrl ?? ""); // Set photo states on edit
                setPhotoError(""); // Set photo states on edit
                setModal("edit");
            },
        });
    };

    if (isLoading) {
        return (
            <StyledWrapper
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "80px 0",
                    color: T.gray400,
                    fontSize: 13,
                    gap: 10,
                }}
            >
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.gray300}
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ animation: "spin 1s linear infinite" }}
                >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                {translate("Loading product types…")}
            </StyledWrapper>
        );
    }

    if (loadError) {
        return isAuthError(loadError) ? (
            <SessionExpiredBanner signInUrl={signInUrl} />
        ) : (
            <StyledWrapper
                style={{
                    background: T.red50,
                    border: `1px solid #fecaca`,
                    borderRadius: 8,
                    padding: "16px 20px",
                    fontSize: 13,
                    color: T.red600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                }}
            >
                <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Ico d={IC.alert} size={15} color={T.red500} />
                    {translate("Failed to load: ")} {loadError}
                </StyledWrapper>
                <Btn variant="secondary" size="sm" onClick={() => window.location.reload()}>
                    {translate("Retry")}
                </Btn>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper data-test-selector="adminProductTypesPage">
            <PageHeader
                title="Product Types"
                subtitle="Product category definitions by division"
                count={productsPagination.totalItemCount}
                search={search}
                onSearch={v => {
                    setSearch(v);
                    if (v === "") {
                        setIsLoading(true);
                        getProductTypeCollectionData({
                            page: 1,
                            pageSize: productsPagination?.pageSize || 50,
                            query: "",
                            onSuccess: result => {
                                setProducts((result.productTypeModels ?? []).map(mapToLocal));
                                setProductsPagination(result.pagination);
                                setIsLoading(false);
                            },
                            onError: error => {
                                setLoadError(error);
                                setIsLoading(false);
                            },
                        });
                    }
                }}
                onSearchClick={handleSearch}
                showSearch={showSearch}
                onClearSearch={() => {
                    setSearch("");
                    getProductTypeCollectionData({
                        page: 1,
                        pageSize: productsPagination?.pageSize || 50,
                        query: "",
                        onSuccess: result => {
                            setProducts((result.productTypeModels ?? []).map(mapToLocal));
                            setProductsPagination(result.pagination);
                        },
                        onError: error => setLoadError(error),
                    });
                }}
                addLabel="New Product Type"
                onAdd={() => {
                    setForm({ name: "", division_id: "" });
                    setSaveError(null);
                    setPhotoFile(null);
                    setPhotoPreviewUrl("");
                    setPhotoError("");
                    setModal("create");
                }}
            />

            {saveError && !modal && (
                <StyledWrapper
                    style={{
                        background: T.red50,
                        border: `1px solid #fecaca`,
                        borderRadius: 7,
                        padding: "10px 14px",
                        marginBottom: 16,
                        fontSize: 13,
                        color: T.red600,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <Ico d={IC.alert} size={14} color={T.red500} />
                    {saveError}
                </StyledWrapper>
            )}
            <StyledWrapper // ← ADD THIS
                style={{
                    width: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    borderRadius: 8,
                }}
            >
                <StyledWrapper style={{ minWidth: 720 }}>
                    <DataTable
                        columns={[
                            {
                                key: "name",
                                label: translate("Product Type"),
                                render: (v: string) => <span style={{ fontWeight: 700, color: T.gray900 }}>{v}</span>,
                            },
                            {
                                key: "division_id",
                                label: translate("Division"),
                                render: (v: string) => {
                                    const division = divisions.find(d => d.id === v); // ← lookup by id
                                    return division ? (
                                        <Badge variant="blue">{division.name}</Badge>
                                    ) : (
                                        <span style={{ color: T.gray300 }}>—</span>
                                    );
                                },
                            },
                        ]}
                        rows={active}
                        onEdit={p => {
                            getProductType(p);
                            setModal("edit");
                        }}
                        onDelete={p => setDel(p)}
                    />
                </StyledWrapper>
            </StyledWrapper>

            {modal && (
                <Modal
                    title={modal === "create" ? translate("New Product Type") : translate("Edit Product Type")}
                    subtitle={modal === "edit" ? form.name : ""}
                    onClose={() => {
                        setModal(null);
                        setSaveError(null);
                        setFieldErrors({});
                        setPhotoFile(null); // set photo default state on close
                        setPhotoPreviewUrl(""); // set photo default state on close
                        setPhotoError(""); // set photo default state on close
                    }}
                    onSave={save}
                    saveLabel={isSaving ? "Saving…" : modal === "create" ? "Create Product Type" : "Save Changes"}
                    // disabled={!form.name?.trim() || !form.division_id?.trim()}
                    maxWidth={460}
                >
                    {saveError && (
                        <StyledWrapper
                            style={{
                                background: saveError === "__AUTH__" ? "#fffbeb" : T.red50,
                                border: `1px solid ${saveError === "__AUTH__" ? "#fcd34d" : "#fecaca"}`,
                                borderRadius: 7,
                                padding: "10px 14px",
                                marginBottom: 16,
                                fontSize: 13,
                                color: saveError === "__AUTH__" ? "#92400e" : T.red600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                            }}
                        >
                            <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <Ico d={IC.alert} size={14} color={saveError === "__AUTH__" ? "#d97706" : T.red500} />
                                {saveError === "__AUTH__"
                                    ? translate("Your session has expired. Please Login again to continue.")
                                    : saveError}
                            </StyledWrapper>
                            {saveError === "__AUTH__" && (
                                <Btn
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        window.location.href = signInUrl || "/";
                                    }}
                                >
                                    {translate("Login")}
                                </Btn>
                            )}
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.formGridContainer} style={{ minHeight: 200 }}>
                        <GridItem width={12} style={{ width: "100%" }}>
                            <Input
                                label={translate("Product Type Name")}
                                value={form.name}
                                onChange={v => {
                                    f("name")(v);
                                    if (v.trim()) setFieldErrors(p => ({ ...p, name: "" })); // ← clear on type
                                }}
                                required
                                disabled={isSaving}
                                error={fieldErrors.name} // ← add this
                            />
                        </GridItem>
                        <GridItem width={12} style={{ width: "100%" }}>
                            <SearchableSelect
                                label={translate("Division")}
                                value={form.division_id}
                                onChange={v => {
                                    f("division_id")(v);
                                    if (v) setFieldErrors(p => ({ ...p, division_id: "" }));
                                }}
                                required
                                error={fieldErrors.division_id}
                                options={divisions
                                    .filter(d => !d.is_deleted)
                                    .map(d => ({ value: d.id, label: d.name }))}
                                placeholder="Search division…"
                            />
                        </GridItem>
                        <GridItem width={12} style={{ width: "100%" }}>
                            <Field
                                label={translate("Product Type Image")}
                                hint={translate("JPG, PNG, WEBP · Max 5MB")}
                            // required
                            // ← NO error prop here anymore
                            >
                                <StyledWrapper
                                    style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}
                                >
                                    {/* Preview — unchanged */}
                                    {photoPreviewUrl ? (
                                        <StyledWrapper style={{ position: "relative", flexShrink: 0 }}>
                                            <img
                                                src={
                                                    photoPreviewUrl.startsWith("data:")
                                                        ? photoPreviewUrl
                                                        : `${origin}/${photoPreviewUrl}`
                                                }
                                                alt="Preview"
                                                style={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: 8,
                                                    objectFit: "cover",
                                                    border: `2px solid ${T.gray200}`,
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={removePhoto}
                                                style={{
                                                    position: "absolute",
                                                    top: -6,
                                                    right: -6,
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: "50%",
                                                    background: T.red500,
                                                    border: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#fff",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                }}
                                            >
                                                ×
                                            </button>
                                        </StyledWrapper>
                                    ) : (
                                        <StyledWrapper
                                            style={{
                                                width: 72,
                                                height: 72,
                                                borderRadius: 8,
                                                background: T.gray100,
                                                border: `2px dashed ${photoError || fieldErrors.image ? T.red500 : T.gray300}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <svg
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke={T.gray400}
                                                strokeWidth="1.5"
                                            >
                                                <path d="M4 16l4-4 4 4 4-6 4 6M3 3h18v14a2 2 0 01-2 2H5a2 2 0 01-2-2V3z" />
                                            </svg>
                                        </StyledWrapper>
                                    )}

                                    {/* Upload button — unchanged */}
                                    <StyledWrapper style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                        <label
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                padding: "7px 14px",
                                                borderRadius: 7,
                                                border: `1.5px solid ${photoError || fieldErrors.image ? T.red500 : T.gray300}`,
                                                background: T.white,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: T.gray600,
                                                cursor: "pointer",
                                                transition: "all 0.13s",
                                            }}
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                                            </svg>
                                            {photoFile ? translate("Change Image") : translate("Upload Image")}
                                            <input
                                                ref={photoUploadRef}
                                                type="file"
                                                accept=".jpg,.jpeg,.png,.webp,.JPG,.JPEG,.PNG,.WEBP"
                                                style={{ display: "none" }}
                                                onChange={handlePhotoChange}
                                                disabled={isSaving}
                                            />
                                        </label>
                                        {photoFile && (
                                            <span style={{ fontSize: 11, color: T.gray400 }}>
                                                {photoFile.name} ({(photoFile.size / 1024).toFixed(0)}KB)
                                            </span>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>

                                {/* ── Manual error message — matches Field's own error style exactly ── */}
                                {(photoError || fieldErrors.image) && (
                                    <span style={{ fontSize: 11, color: T.red500, marginTop: 4, display: "block" }}>
                                        {photoError || fieldErrors.image}
                                    </span>
                                )}
                            </Field>
                        </GridItem>
                    </GridContainer>
                </Modal>
            )}

            {del && (
                <ConfirmModal
                    message={translate(`Delete product type "${del.name}"?`)}
                    onConfirm={() => {
                        deleteProductTypeData({
                            productTypeId: del.id,
                            onSuccess: () => {
                                setProducts(p => p.filter(x => x.id !== del.id));
                                setDel(null);
                                setRefreshCount(c => c + 1);
                                setSearch("")
                            },
                            onError: (error: string) => {
                                setSaveError(error);
                                setDel(null);
                            },
                        });
                    }}
                    onCancel={() => setDel(null)}
                />
            )}
            {products.length > 0 && (
                <Pagination
                    {...styles.pagination}
                    currentPage={productsPagination?.currentPage || 1}
                    resultsPerPage={productsPagination?.pageSize}
                    resultsCount={productsPagination?.totalItemCount || 0}
                    resultsPerPageOptions={productsPagination?.pageSizeOptions}
                    onChangePage={changePageHandler}
                    onChangeResultsPerPage={changeResultsPerPageHandler}
                    prevPage={productsPagination?.prevPageUri}
                    nextPage={productsPagination?.nextPageUri}
                />
            )}
        </StyledWrapper>
    );
};
// ─── BRANDS PAGE PROPS INTERFACE ─────────────────────────────────────────────
// Add this alongside BranchesPageProps, DivisionsPageProps, etc.

// ─── BRANDS PAGE ──────────────────────────────────────────────────────────────

const BrandsPage: FC<BrandsPageProps> = ({ getBrandsList, getBrandListsById, showSearch }) => {
    const [selectedBrandCu, setSelectedBrandCu] = useState<BrandsSmeModel | null>(null);

    const handleBrandClick = useCallback(
        (id: string) => {
            getBrandListsById({
                id,
                onSuccess: setSelectedBrand,
                onError: () => setSelectedBrand(null),
            });
        },
        [getBrandListsById],
    );
    const [brands, setBrands] = useState<any[]>([]);
    const [brandsPagination, setBrandsPagination] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [search, setSearch] = useState("");

    // ── Detail modal state ─────────────────────────────────────────────
    const [selectedBrand, setSelectedBrand] = useState<any | null>(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    const origin = "userfiles";

    // ── Map API → local ────────────────────────────────────────────────
    const mapToLocal = (b: BrandsSmeModel) => ({
        id: b.id,
        name: b.name,
        imageUrl: b.imageUrl ?? "",
        brandAlias: b.brandAlias ?? "",
        is_deleted: false,
    });

    // ── Load on mount ──────────────────────────────────────────────────
    React.useEffect(() => {
        setIsLoading(true);
        setLoadError(null);
        getBrandsList({
            page: 1,
            onSuccess: (result: BrandListModel) => {
                setBrands((result.brandModels ?? []).map(mapToLocal));
                setBrandsPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    }, []);

    // ── Pagination handlers ────────────────────────────────────────────
    const changePageHandler = (newPageIndex: number) => {
        getBrandsList({
            page: newPageIndex,
            pageSize: brandsPagination?.pageSize,
            query: search,
            onSuccess: (result: BrandListModel) => {
                setBrands((result.brandModels ?? []).map(mapToLocal));
                setBrandsPagination(result.pagination);
            },
            onError: (error: string) => {
                setLoadError(error);
            },
        });
    };

    const changeResultsPerPageHandler = (event: React.ChangeEvent<HTMLSelectElement>, resultsPerPage: number) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        getBrandsList({
            page: 1,
            pageSize: newPageSize,
            onSuccess: (result: BrandListModel) => {
                setBrands((result.brandModels ?? []).map(mapToLocal));
                setBrandsPagination(result.pagination);
            },
            onError: (error: string) => {
                setLoadError(error);
            },
        });
    };

    // ── Search ─────────────────────────────────────────────────────────
    const handleSearch = () => {
        setIsLoading(true);
        setLoadError(null);
        getBrandsList({
            page: 1,
            pageSize: brandsPagination?.pageSize || 50,
            query: search.trim(),
            onSuccess: (result: BrandListModel) => {
                setBrands((result.brandModels ?? []).map(mapToLocal));
                setBrandsPagination(result.pagination);
                setIsLoading(false);
            },
            onError: (error: string) => {
                setLoadError(error);
                setIsLoading(false);
            },
        });
    };

    // ── Open brand detail ──────────────────────────────────────────────
    // const handleBrandClick = (brand: any) => {
    //     setSelectedBrand(null);
    //     setDetailError(null);
    //     setIsDetailLoading(true);

    //     // getBrandByIdHandler({
    //     //     id: brand?.id,
    //     //     onSuccess: (result: BrandsModel) => {
    //     //         setSelectedBrand(mapToLocal(result));
    //     //         setIsDetailLoading(false);
    //     //     },
    //     //     onError: (error: string) => {
    //     //         setDetailError(error);
    //     //         setIsDetailLoading(false);
    //     //     },
    //     // });
    // };

    const activeBrands = brands.filter(b => !b.is_deleted);
    //     const filtered = brands.filter(
    //     b => `${b.name} ${b.city} ${b.state}`.toLowerCase().includes(search.toLowerCase()),
    // );

    // ── Loading ────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <StyledWrapper
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "80px 0",
                    color: T.gray400,
                    fontSize: 13,
                    gap: 10,
                }}
            >
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.gray300}
                    strokeWidth="2"
                    strokeLinecap="round"
                    style={{ animation: "spin 1s linear infinite" }}
                >
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                {translate("Loading brands…")}
            </StyledWrapper>
        );
    }

    // ── Load error ─────────────────────────────────────────────────────
    if (loadError) {
        return isAuthError(loadError) ? (
            <SessionExpiredBanner />
        ) : (
            <StyledWrapper
                style={{
                    background: T.red50,
                    border: `1px solid #fecaca`,
                    borderRadius: 8,
                    padding: "16px 20px",
                    fontSize: 13,
                    color: T.red600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                }}
            >
                <StyledWrapper style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Ico d={IC.alert} size={15} color={T.red500} />
                    {translate("Failed to load brands: ")} {loadError}
                </StyledWrapper>
                <Btn
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                        setIsLoading(true);
                        setLoadError(null);
                        getBrandsList({
                            page: 1,
                            onSuccess: (result: BrandListModel) => {
                                setBrands((result.brandModels ?? []).map(mapToLocal));
                                setBrandsPagination(result.pagination);
                                setIsLoading(false);
                            },
                            onError: (error: string) => {
                                setLoadError(error);
                                setIsLoading(false);
                            },
                        });
                    }}
                >
                    {translate("Retry")}
                </Btn>
            </StyledWrapper>
        );
    }

    // ── Render ─────────────────────────────────────────────────────────
    return (
        <StyledWrapper data-test-selector="adminBrandsPage">
            {/* ── Page header (title + count + search — no "New" button needed) ── */}

            <PageHeader
                title="Brands"
                subtitle="See all brands"
                count={brandsPagination.totalItemCount}
                search={search}
                onSearch={v => {
                    setSearch(v);
                    if (v === "") {
                        setIsLoading(true);
                        getBrandsList({
                            page: 1,
                            pageSize: brandsPagination?.pageSize || 50,
                            query: "",
                            onSuccess: result => {
                                setBrands((result.brandModels ?? []).map(mapToLocal));
                                setBrandsPagination(result.pagination);
                                setIsLoading(false);
                            },
                            onError: error => {
                                setLoadError(error);
                                setIsLoading(false);
                            },
                        });
                    }
                }}
                showSearch={showSearch}
                onSearchClick={handleSearch}
                onClearSearch={() => {
                    setSearch("");
                    getBrandsList({
                        page: 1,
                        pageSize: brandsPagination?.pageSize || 50,
                        query: "",
                        onSuccess: result => {
                            setBrands((result.brandModels ?? []).map(mapToLocal));
                            setBrandsPagination(result.pagination);
                        },
                        onError: error => setLoadError(error),
                    });
                }}
            />

            {/* ── Table ── */}
            <StyledWrapper
                style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch", borderRadius: 8 }}
            >
                <StyledWrapper style={{ minWidth: 400 }}>
                    <StyledWrapper {...styles.tableWrapper}>
                        <StyledWrapper style={{ minWidth: 600 }}>
                            <DataTableCustom
                                columns={[
                                    {
                                        key: "name",
                                        label: translate("Brand"),
                                        render: (v: string) => (
                                            <span style={{ fontWeight: 700, color: T.gray900 }}>{v}</span>
                                        ),
                                    },
                                    {
                                        key: "brandAlias",
                                        label: translate("Alias"),
                                        render: (v: string) => (
                                            <span style={{ color: T.gray700 }}>{v || "-"}</span>
                                        ),
                                    },
                                ]}
                                rows={activeBrands}
                                onRowClick={(row: any) => handleBrandClick(row.id)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            {/* ── Pagination ── */}
            {brands.length > 0 && (
                <Pagination
                    {...styles.pagination}
                    currentPage={brandsPagination?.currentPage || 1}
                    resultsPerPage={brandsPagination?.pageSize}
                    resultsCount={brandsPagination?.totalItemCount || 0}
                    resultsPerPageOptions={brandsPagination?.pageSizeOptions}
                    onChangePage={changePageHandler}
                    onChangeResultsPerPage={changeResultsPerPageHandler}
                    prevPage={brandsPagination?.prevPageUri}
                    nextPage={brandsPagination?.nextPageUri}
                />
            )}

            {/* ── Brand Detail Modal ─────────────────────────────────── */}
            {(isDetailLoading || selectedBrand || detailError) && (
                <StyledWrapper {...styles.modalOverlayWrapper}>
                    <StyledWrapper {...styles.modalContentWrapper} style={{ maxWidth: 420 }}>
                        {/* Header */}
                        <StyledWrapper {...styles.modalHeaderWrapper}>
                            <StyledWrapper>
                                <StyledWrapper
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 800,
                                        color: T.gray900,
                                        letterSpacing: "-0.02em",
                                    }}
                                >
                                    {translate("Brand Details")}
                                </StyledWrapper>
                                {selectedBrand && (
                                    <StyledWrapper style={{ fontSize: 12, color: T.gray400, marginTop: 3 }}>
                                        {selectedBrand.name}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                            <Button
                                onClick={() => {
                                    setSelectedBrand(null);
                                    setDetailError(null);
                                    setIsDetailLoading(false);
                                }}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: T.gray400,
                                    padding: 3,
                                    borderRadius: 5,
                                    lineHeight: 1,
                                }}
                            >
                                <Ico d={IC.x} size={17} />
                            </Button>
                        </StyledWrapper>

                        {/* Body */}
                        <StyledWrapper
                            style={{
                                padding: "28px 24px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 20,
                                alignItems: "center",
                            }}
                        >
                            {/* Loading spinner */}
                            {isDetailLoading && (
                                <StyledWrapper
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "40px 0",
                                        color: T.gray400,
                                        fontSize: 13,
                                        gap: 10,
                                    }}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke={T.gray300}
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        style={{ animation: "spin 1s linear infinite" }}
                                    >
                                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                                    </svg>
                                    {translate("Loading brand…")}
                                </StyledWrapper>
                            )}

                            {/* Detail error */}
                            {detailError && !isDetailLoading && (
                                <StyledWrapper
                                    style={{
                                        background: T.red50,
                                        border: `1px solid #fecaca`,
                                        borderRadius: 8,
                                        padding: "12px 16px",
                                        fontSize: 13,
                                        color: T.red600,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        width: "100%",
                                    }}
                                >
                                    <Ico d={IC.alert} size={14} color={T.red500} />
                                    {translate("Failed to load brand details.")}
                                </StyledWrapper>
                            )}

                            {/* Brand detail content */}
                            {selectedBrand && !isDetailLoading && (
                                <>
                                    {/* Brand image */}
                                    {selectedBrand.imageURL ? (
                                        <img
                                            src={
                                                selectedBrand.imageURL
                                                    ? selectedBrand.imageURL
                                                    : `${selectedBrand.imageURL}`
                                            }
                                            alt={selectedBrand.name}
                                            style={{
                                                width: 120,
                                                height: 120,
                                                objectFit: "contain",
                                                borderRadius: 12,
                                                border: `1.5px solid ${T.gray200}`,
                                                background: T.gray50,
                                                padding: 8,
                                            }}
                                        />
                                    ) : (
                                        <StyledWrapper
                                            style={{
                                                width: 120,
                                                height: 120,
                                                borderRadius: 12,
                                                background: T.blue50,
                                                border: `1.5px solid ${T.blue100}`,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Ico d={IC.brand} size={40} color={T.blue500} sw={1.2} />
                                        </StyledWrapper>
                                    )}

                                    {/* Brand name */}
                                    <StyledWrapper
                                        style={{
                                            fontSize: 18,
                                            fontWeight: 800,
                                            color: T.gray900,
                                            letterSpacing: "-0.02em",
                                            textAlign: "center",
                                        }}
                                    >
                                        {selectedBrand.name}
                                    </StyledWrapper>

                                    {/* Brand alias */}
                                    {selectedBrand.brandAlias && (
                                    <StyledWrapper
                                        style={{
                                            fontSize: 18,
                                            fontWeight: 800,
                                            color: T.gray900,
                                            letterSpacing: "-0.02em",
                                            textAlign: "center",
                                        }}
                                    >
                                        Alias - {selectedBrand.brandAlias}
                                    </StyledWrapper>
                                    )}
                                </>
                            )}
                        </StyledWrapper>

                        {/* Footer */}
                        <StyledWrapper {...styles.modalFooterWrapper}>
                            <Btn
                                variant="secondary"
                                onClick={() => {
                                    setSelectedBrand(null);
                                    setDetailError(null);
                                    setIsDetailLoading(false);
                                }}
                            >
                                {translate("Close")}
                            </Btn>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
// ─── DASHBOARD PAGE ───────────────────────────────────────────────────────────

// ─── NAV ITEMS ────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
    { id: "branches", label: "Branches", icon: "branch" },
    { id: "employees", label: "Employees", icon: "emp" },
    { id: "brands", label: "Brands", icon: "brand" },
    { id: "divisions", label: "Divisions", icon: "div" },
    { id: "products", label: "Product Types", icon: "product" },
];

// ─── MAIN WIDGET COMPONENT ────────────────────────────────────────────────────

const VALID_PAGES = ["employees", "branches", "divisions", "brands", "products"];

const AdminPortalWidget: FC<Props> = ({
    fields: widgetFields,
    addBranchData,
    getBranchCollectionData,
    updateBranchData,
    deleteBranchData,
    addProductTypeData,
    getProductTypeById,
    getProductTypeCollectionData,
    updateProductTypeData,
    deleteProductTypeData,
    addDivisionData,
    getDivisionCollectionData,
    updateDivisionData,
    deleteDivisionData,
    addEmployeeData,
    getEmployeeCollectionData,
    updateEmployeeData,
    deleteEmployeeData,
    session,
    getBrandsCollectionData,
    getEmployeesById,
    getBranchByIdHandler,
    getDivisionByIdHandler,
    getBrandsList,
    getBrandListsById,
    signInUrl,
}) => {
    // Ensure initial page is valid, default to "employees"
    const initialPage =
        widgetFields.initialPage && VALID_PAGES.includes(widgetFields.initialPage)
            ? widgetFields.initialPage
            : "employees";
    // In AdminPortalWidget, update setPage to also reset URL params

    const [page, setPage] = useState<string>("branches");
    const [liveDivisions, setLiveDivisions] = useState<{ id: string; name: string; is_deleted: boolean }[]>([]);
    const [liveBranches, setLiveBranches] = useState<any[]>([]);
    const handlePageChange = (newPage: string) => {
        setPage(newPage);

        // Reset URL query params when switching tabs
        const url = new URL(window.location.href);
        url.searchParams.delete("page");
        url.searchParams.delete("pageSize"); // optional, if you track this too
        window.history.replaceState({}, "", url.toString());
    };

    const hasSMEAdminRole = /\bSME-ADMIN\b/.test(session?.userRoles);
    const hasCentroEmployeeRole = /\bCentro-Employee\b/.test(session?.userRoles);
    React.useEffect(() => {
        if (!hasSMEAdminRole) {
            window.location.href = "/";
        }
    }, [session]);
    if (!hasSMEAdminRole) return null;

    return (
        <>
            <link
                href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap"
                rel="stylesheet"
            />
            <StyledWrapper {...styles.portalWrapper} data-test-selector="adminPortal">
                {/* ── Sidebar ─────────────────────────────────────────── */}
                <StyledWrapper {...styles.sidebarWrapper}>
                    <StyledWrapper {...styles.sidebarLogoWrapper}>
                        <StyledWrapper
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: COLORS.primary.base,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow: "0 2px 6px rgba(59,110,245,0.3)",
                                flexShrink: 0,
                            }}
                        >
                            <Ico d={IC.link} size={15} color="#fff" sw={2.2} />
                        </StyledWrapper>
                        <StyledWrapper>
                            <StyledWrapper
                                style={{ fontSize: 13.5, fontWeight: 800, color: T.gray900, letterSpacing: "-0.01em" }}
                            >
                                {translate("Admin Portal")}
                            </StyledWrapper>
                            <StyledWrapper
                                style={{
                                    fontSize: 9.5,
                                    color: T.gray400,
                                    letterSpacing: "0.05em",
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                }}
                            >
                                {translate("Management System")}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.sidebarNavWrapper}>
                        <StyledWrapper
                            style={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: T.gray300,
                                letterSpacing: "0.09em",
                                padding: "6px 8px 7px",
                                textTransform: "uppercase",
                            }}
                        >
                            {translate("Menu")}
                        </StyledWrapper>
                        {NAV_ITEMS.map(item => {
                            const on = page === item.id;
                            return (
                                <Button
                                    key={item.id}
                                    onClick={() => handlePageChange(item.id)}
                                    data-test-selector={`navItem_${item.id}`}
                                    {...styles.buttonsWrapper}
                                    // style={
                                    //     on
                                    //         ? {
                                    //               outlineOffset: "1px",
                                    //               outlineColor: `${COLORS.primary.light} !important`,
                                    //               outlineStyle: "solid",
                                    //               outlineWidth: "2px",
                                    //           }
                                    //         : {}
                                    // }
                                    style={on ? { background: T.gray50 } : {}}
                                    onMouseEnter={e => {
                                        if (!on) {
                                            (e.currentTarget as HTMLButtonElement).style.background = T.gray50;
                                            (e.currentTarget as HTMLButtonElement).style.color = COLORS.background.tag;
                                        }
                                    }}
                                    onMouseLeave={e => {
                                        if (!on) {
                                            (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                                            (e.currentTarget as HTMLButtonElement).style.color = T.gray500;
                                        }
                                    }}
                                >
                                    <Ico d={IC[item.icon]} size={15} color="currentColor" />
                                    {/* <Icon {...styles.titleIcon} src={icon} /> */}
                                    {/* <PeopleIcon /> */}
                                    {/* <SMEAdminHomeIcon /> */}
                                    {translate(item.label)}
                                </Button>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>

                {/* ── Main Content ─────────────────────────────────────── */}
                <StyledWrapper {...styles.mainContentWrapper}>
                    {/* {page === "dashboard" && <DashboardPage showStats={widgetFields.showDashboardStats} />} */}
                    {page === "employees" && (
                        <EmployeesPage
                            showSearch={widgetFields.showSearchBar}
                            addEmployeeData={addEmployeeData}
                            getEmployeeCollectionData={getEmployeeCollectionData}
                            updateEmployeeData={updateEmployeeData}
                            deleteEmployeeData={deleteEmployeeData}
                            getBranchCollectionData={getBranchCollectionData}
                            getDivisionCollectionData={getDivisionCollectionData}
                            getProductTypeCollectionData={getProductTypeCollectionData}
                            getBrandsCollectionData={getBrandsCollectionData}
                            getEmployeesById={getEmployeesById}
                            signInUrl={signInUrl}
                        />
                    )}
                    {page === "branches" && (
                        <BranchesPage
                            showSearch={widgetFields.showSearchBar}
                            addBranchData={addBranchData}
                            getBranchCollectionData={getBranchCollectionData}
                            updateBranchData={updateBranchData}
                            deleteBranchData={deleteBranchData}
                            getBranchByIdHandler={getBranchByIdHandler}
                            signInUrl={signInUrl}
                        />
                    )}
                    <>
                        {page === "divisions" && (
                            <DivisionsPage
                                addDivisionData={addDivisionData}
                                getDivisionCollectionData={getDivisionCollectionData}
                                updateDivisionData={updateDivisionData}
                                deleteDivisionData={deleteDivisionData}
                                onDivisionsLoaded={setLiveDivisions} // ← keeps liveDivisions in sync
                                getBrandsCollectionData={getBrandsCollectionData}
                                getDivisionByIdHandler={getDivisionByIdHandler}
                                signInUrl={signInUrl}
                            />
                        )}
                        {page === "products" && (
                            <ProductTypesPage
                                addProductTypeData={addProductTypeData}
                                getProductTypeById={getProductTypeById} // ← pass directly, no wrapper needed
                                getProductTypeCollectionData={getProductTypeCollectionData}
                                updateProductTypeData={updateProductTypeData}
                                deleteProductTypeData={deleteProductTypeData}
                                divisions={liveDivisions}
                                getDivisionCollectionData={getDivisionCollectionData}
                                signInUrl={signInUrl}
                                showSearch={widgetFields.showSearchBar}
                            />
                        )}
                        {page === "brands" && (
                            <BrandsPage
                                getBrandsList={getBrandsList}
                                getBrandListsById={getBrandListsById}
                                showSearch={widgetFields.showSearchBar}
                            />
                        )}
                    </>

                    {/* {page === "products" && (
                        <ProductTypesPage
                            addProductTypeData={addProductTypeData}
                            getProductTypeCollectionData={getProductTypeCollectionData}
                            updateProductTypeData={updateProductTypeData}
                            deleteProductTypeData={deleteProductTypeData}
                            divisions={initDivisions} // ← pass static for now; replace with API later
                        />
                    )} */}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AdminPortalWidget),
    definition: {
        group: "Basic",
        displayName: "Admin Portal",

        fieldDefinitions: [
            {
                name: fields.initialPage,
                editorTemplate: "DropDownField",
                displayName: "Default Landing Page",
                defaultValue: "employees",
                fieldType: "General",
                options: [
                    { displayName: "Employees", value: "employees" },
                    { displayName: "Branches", value: "branches" },
                    { displayName: "Divisions", value: "divisions" },
                    { displayName: "Product Types", value: "products" },
                ],
            },
            {
                name: fields.showSearchBar,
                displayName: "Show Search Bar",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showDashboardStats,
                displayName: "Show Dashboard Statistics",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.adminDisplayName,
                displayName: "Admin Display Name",
                editorTemplate: "TextField",
                defaultValue: "Admin User",
                fieldType: "General",
            },
            {
                name: fields.adminEmail,
                displayName: "Admin Email",
                editorTemplate: "TextField",
                defaultValue: "admin@company.com",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
